import { keepPreviousData } from "@tanstack/react-query"
import { headerNamesByColId } from "~/components/planning/columnChooser/columnChooserModel"
import { useOpenColumnChooserCommand } from "~/components/planning/columnChooser/openColumnChooser"
import { useRouter } from "@tanstack/react-router"
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useSessionStorage } from "usehooks-ts"
import { type AgGridReact, type CustomCellRendererProps } from "ag-grid-react"
import {
  type ColDef,
  type GetRowIdFunc,
  type GetRowIdParams,
  type GridOptions,
  type ICellRendererParams,
  type IRowNode,
  type RowSelectionOptions,
} from "ag-grid-community"
import {
  createSizeDimensionColumn,
  CommentsCellWrapperClassName,
  compareWithNullsLast,
  createActionsColumn,
  createAverageColumn,
  createGroupedStringColumn,
  createNumberColumn,
  createSizeSplitConfidenceColumn,
  createStringColumn,
  createSumColumn,
  createUniqueNumberColumn,
  createUniqueStringColumn,
} from "~/utils/columnTemplates"
import { first, flatten, mean, meanBy, round, sum, uniqBy } from "lodash"

import { Button, IconButton } from "~/components/reusable/Button"
import { col, type RelationalFormulaContext } from "~/components/reusable/table/relationalFormula"
import { QuantityDiffCell } from "~/components/reusable/table/cells/QuantityDiffCell"
import { useDisabledColumnIdSet } from "~/hooks/useDisabledColumns"
import { useForecasts } from "~/hooks/useForecasts"
import { deriveLeadTimeAvailability, leadTimeKey } from "~/utils/stockProjection"
import {
  getStockOutTimesV2,
  isExtendedStockOutResult,
  type StockOutResult,
} from "~/utils/calculateStockoutTimes"
import { syncToPreorders, syncToUnfulfillablePreorders } from "~/utils/purchaseOrderSync"
import { changedCells, hasColliViolation } from "~/utils/quantityViolations"
import { colliLines, formatGroupVariableName, violationMessages } from "~/utils/violationMessages"
import {
  useQuantityViolations,
  useSkuConstraints,
  type ViolationResolver,
} from "~/hooks/useQuantityViolations"
import { ResolveViolationsToolbar } from "~/components/planning/ResolveViolationsButtons"
import { adjustPurchaseOrder, type POSkuData } from "~/hooks/usePurchaseOrdersV2"
import { useStockOutTimesDetails, useStockOutTimesV2 } from "~/hooks/useStockOutTimesV2"
import {
  useCollectionSkuExtraDataKeys,
  useExtraDataAggregationMap,
  useExtraDataDisplayNameMap,
  useExtraDataFormattedKeys,
  useExtraDataTypeMap,
} from "~/hooks/useSku"
import { api } from "~/utils/api"
import {
  DateFormatType,
  differenceInCalendarDaysUTC,
  differenceInWeeksFloat,
  formatDate,
  getStartOfCurrentWeekUTC,
  notBeforeTodayUTC,
  startOfTodayUTC,
} from "~/utils/datetime"
import { calculateProjectedCover, poolAddedDemandWarehouses } from "~/utils/weeksOnHand"
import {
  formatForecast,
  formatNumber,
  formatSuggestedOrder,
  formatUniqueValues,
  roundForecast,
  sizeValueFormatter,
  uniqueValueFormatter,
} from "~/utils/formatting"
import { cn, formatSkuId, formatVariantId } from "~/utils/helpers"
import { openProduct } from "~/utils/routerUtils"
import { filterDefined, largest, sumBy, groupBy, uniq } from "~/utils/tsUtils"
import { selectBestImageUrlFromObjects } from "~/utils/imageUtils"
import { filterOnSearchString } from "~/utils/search"
import { IncomingOrderCellRenderer } from "./incomingOrders/IncomingOrderCellRenderer"
import type { POIncomingOrder } from "~/utils/incomingOrdersUtils"
import { createPOMonthCoverColumn } from "./monthCoverColumns"
import { ProjectedWeeksOnHandField } from "./ProjectedWeeksOnHandField"
import { ProjectedStockEADField } from "./ProjectedStockEADField"
import { ProjectedStockEOPField } from "./ProjectedStockEOPField"
import { dynamicTableColumnsV2 } from "~/components/planning/dynamicTableData/DynamicTableColumns"
import { CommentsHoverCard } from "../product/CommentHoverCard"
import { ProductCellContent } from "../product/ProductCellContent"
import { AggregationMethod } from "../reusable/AggregationSelector"
import { DataTableBaseV2 } from "../reusable/DataTableV2"
import { NpiForecastValue } from "../reusable/NpiForecastValue"
import { Tooltip } from "../reusable/Tooltip"
import { TrashSimple, Warning, WarningCircle } from "@phosphor-icons/react"
import { formatName } from "~/utils/namingUtils"
import { isQuantityLockedPOStatus } from "~/utils/purchaseOrders"
import { sortSizesComparator } from "~/utils/sizeSplit"
import { keepSkusInSizeOrder } from "~/components/noos-supply-plan/buy-detail/buyColumns"

import { PurchaseOrderTableColumn } from "~/constants/purchaseOrderTable"
import { getColumnNaming } from "~/components/planning/ProductTable/utils/columnUtils"
import { ProductTableColumn } from "~/constants/productTableColumns"
import { IMPUTATION_WARNING_THRESHOLD } from "~/utils/sizeSplitConfidence"
import {
  useIsCarharttWip,
  getCarharttSimulatedAvailabilityColumn,
  getCarharttAvailabilityViolationColumn,
} from "./carharttColumns"
import { PURCHASE_ORDER_UPDATE_TABLE_ID } from "~/constants"
import { poChooserCopy } from "./poChooserCopy"
import { useRegisterTable } from "~/components/planning/TableRegistryContext"
import { useGridOwnedColumns } from "~/hooks/useGridOwnedColumns"
import { useShownColumnIds } from "~/components/planning/ProductTable/useProductTableColumns"
import { useNpiVariants } from "~/hooks/useVariants"
import { useUnfulfillablePreordersForWarehouses } from "~/hooks/useUnfulfillablePreorders"
import { computeUnfulfillableBySkuBeforeDate } from "~/utils/unfulfillablePreorderUtils"
import { QualityCellRenderer } from "../forecastEvaluation/QualityCellRenderer"
import { DemandInPeriodHoverCard } from "./DemandInPeriodHoverCard"
import { PreordersInPeriodHoverCard } from "./PreordersInPeriodHoverCard"
import ShortfallSyncMenuItems from "./ShortfallSyncMenuItems"
import { useColumnAvailability } from "../planning/ProductTable/utils/columnAvailability"
import { useFlags } from "~/features/feature-flags"
import { addDays, addWeeks, isAfter, isBefore, subDays, subWeeks } from "date-fns"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "../reusable/DropdownMenu"
import InfoTooltip from "../reusable/InfoTooltip"
import ExpandableSearchField from "../planning/ExpandableSearchField"
import ViewConfiguration from "../planning/ViewConfiguration"
import TagBadge from "../planning/Tags/TagBadge"
import { Badge } from "~/components/reusable/Badge"
import { IVariantSummary } from "~/hooks/useVariantTableData"
import {
  useInventoryTurnData,
  useSalesSummaries,
  useSkuInventoryTurnData,
} from "~/hooks/useVariantTableHooks"
import { inventoryTurnColumns } from "./inventoryTurnColumns"
import CustomAgGridTooltip from "../reusable/table/tooltips/CustomTooltip"
import type { SafetyStockTooltipValue } from "../reusable/table/tooltips/SafetyStockTooltip"
import type { IStockOutTimesDetail } from "~/models/stockOutTimes"
import { getForecastBetweenWeeks, getForecastedReturnsBetweenWeeks } from "~/utils/forecastUtils"
import { subscribeGridValueChange, useGridStateStorage } from "~/hooks/useGridState"
import { storageLocationColumns } from "../planning/dynamicTableData/DynamicTableColumns"
import { useStorageLocationDataVariant } from "~/hooks/useStorageLocationData"
import { DocumentType } from "~/constants/documentType"
import { DocumentCommentsButton } from "../reusable/DocumentCommentsDrawer"
import {
  SelectedVariantsProvider,
  useSelectedVariantHandlers,
  useSelectedVariantState,
} from "~/context/SelectedVariantsProvider"
import MultiEditPurchaseOrderSkuPanel from "./MultiEditPurchaseOrderSkuPanel"

// Group key mirrors `getIncomingOrderDetails()` so size-split SKUs of the same production order
// collapse (merged skuInfo), but distinct orders/dates/statuses stay separate — otherwise the
// date-bucket columns mis-attribute quantities and the cell renders only one status tile.
function aggregateIncomingOrders(orders: POIncomingOrder[]): POIncomingOrder[] {
  const grouped = groupBy(
    orders,
    (o) =>
      `${o.warehouseId}|${o.variantId}|${o.productionOrderId}|${o.expectedArrivalDate.getTime()}|${o.status}`,
  )
  return Object.values(grouped).map((group) => ({
    ...group[0]!,
    quantity: sumBy(group, "quantity"),
    cost: sumBy(group, (o) => o.cost ?? 0),
    skuInfo: group.reduce(
      (acc, order) => ({ ...acc, ...order.skuInfo }),
      {} as Record<string, number>,
    ),
  }))
}

function getIncomingOrdersInSafetyStockPeriod(
  incomingOrders: POIncomingOrder[],
  purchasePeriodEnd: Date,
  safetyStockEnd: Date | undefined,
): POIncomingOrder[] {
  if (!safetyStockEnd) return []
  return incomingOrders.filter(
    (o) =>
      o.status !== "arrived" &&
      isAfter(o.expectedArrivalDate, purchasePeriodEnd) &&
      isBefore(o.expectedArrivalDate, safetyStockEnd),
  )
}

/**
 * Sum of incoming-order units arriving strictly within the purchase period (after this
 * order's expected arrival, before the next order's arrival). Used as the "incoming during
 * period" component of projected-stock-at-arrival when balancing quantities across SKUs.
 */
function computeIncomingDuringPeriod(
  incomingOrders: POIncomingOrder[],
  expectedArrivalDate: Date,
  nextOrderArrivalDate: Date,
): number {
  return incomingOrders
    .filter(
      (o) =>
        isAfter(o.expectedArrivalDate, expectedArrivalDate) &&
        isBefore(o.expectedArrivalDate, nextOrderArrivalDate),
    )
    .reduce((s, o) => s + o.quantity, 0)
}

function getIncomingSafetyStockWarnings(incomingDuringSafetyStock: POIncomingOrder[]): {
  warnings: string[]
  suggestedActions: string[]
} {
  if (incomingDuringSafetyStock.length === 0) return { warnings: [], suggestedActions: [] }
  const totalQuantity = incomingDuringSafetyStock.reduce((s, o) => s + o.quantity, 0)
  const n = incomingDuringSafetyStock.length
  return {
    warnings: [
      `${n === 1 ? "An incoming order" : `${n} incoming orders`} (${totalQuantity} units) arrive${n === 1 ? "s" : ""} during the safety stock period, risking overstock.`,
    ],
    suggestedActions: ["Consider manually adjusting the quantity to avoid overstock."],
  }
}

export interface RowDataType extends POSkuData {
  id: string
  // TODO: Where do I need these here? They are also a part of POSkuData?
  skuId: string
  variantId: string
  quantity: number
  item: {
    skuId: string
    quantity: number
    warehouseId: string
    entries: { skuId: string; warehouseId: string; quantity: number }[]
  }
  index: number
  forecastsPurchasePeriod: number
  forecastTwelveMonths: number
  salesQuantity: number
  preordersPurchasePeriod: number
  preordersBeforeEAD: number
  unfulfillableBeforeEAD: number
  atRiskBeforeEAD: number
  warehouseId: string
  suggestedOrderQuantity: number
  suggestedOrderRawQuantity: number
  suggestedOrderRawQuantityWithNegative: number
  suggestedOrderMoqQuantity: number
  outOfStockInformation?: StockOutResult
  secondaryMoqGrouper: string
  lostSalesWithinLeadTime: number
  isNpi?: boolean
  variantStockoutImputedFraction?: number | null
  skuStockoutImputedFraction?: number | null
  projectedStockAtArrival: number
  projectedStockAtArrivalBeforeOrder?: number
  weeksInPurchasePeriod: number
  /**
   * Weeks of cover from arrival: `Infinity` when stock never depletes, `null` when the purchase
   * period carries no demand and cover therefore has no value.
   */
  avgWeeksOnHandAfterArrival: number | null
  /** False while the cover simulation has no detail for the SKU, so `null` cover isn't "no demand" yet. */
  coverLoaded: boolean
  /** Loaded with no forecast for the SKU's warehouse: renders as no demand. */
  noForecast: boolean
  /** Projected stock at arrival on the demand scope the cover is simulated against. */
  stockAtArrivalForCover: number
  stockoutBeforeArrival: boolean
  /** The stock-out that ends `avgWeeksOnHandAfterArrival`, not just the next one after arrival. */
  stockOutDateAfterArrival: Date | undefined
  incomingDuringPeriod: number
  incomingDuringSafetyStock: POIncomingOrder[]
  expectedReturnsInPeriod: number
  /** Returns landing up to ETA on the primary warehouse, the scope of `projectedStockAtArrivalBeforeOrder`. */
  expectedReturnsBeforeEAD: number
  projectedStockEOP: number
  purchasePeriodLostSales: number
  revenue: number
  inventoryTurnCogs: number
  inventoryTurnAvgInvValue: number
  inventoryTurnStartDate: Date | string | null
}

const NO_DEMAND_TOOLTIP = "No expected demand between arrival and next order arrival."
const NO_FORECAST_TOOLTIP = "No expected demand for this variant in this warehouse."

const coverLeaves = (node: IRowNode<RowDataType>) =>
  (node.group ? (node.allLeafChildren ?? []) : [node]).filter((leaf) => leaf.data?.coverLoaded)

/** Loaded, and no loaded leaf has demand in the purchase period. Still-loading rows never qualify. */
function isNoDemandNode(node: IRowNode<RowDataType>): boolean {
  const leaves = coverLeaves(node)
  return leaves.length > 0 && leaves.every((leaf) => leaf.data?.avgWeeksOnHandAfterArrival == null)
}

function noDemandTooltip(node: IRowNode<RowDataType>): string | undefined {
  if (!isNoDemandNode(node)) return undefined
  return coverLeaves(node).every((leaf) => leaf.data?.noForecast)
    ? NO_FORECAST_TOOLTIP
    : NO_DEMAND_TOOLTIP
}

/** Cover the cell renders: mean of depleting leaves, `Infinity` (52+) if none deplete, `null` for no demand. */
function resolveGroupCover(node: IRowNode<RowDataType>): number | null {
  const valued = coverLeaves(node)
    .map((leaf) => leaf.data?.avgWeeksOnHandAfterArrival)
    .filter((value): value is number => value != null)
  if (!valued.length) return null
  const finite = valued.filter(Number.isFinite)
  return finite.length ? mean(finite) : Infinity
}

const SkuCellRenderer: React.FC<ICellRendererParams<RowDataType>> = (params) => {
  return (
    <div className="flex flex-col items-start justify-center h-[40px] w-[100%] leading-5">
      <div className="label-sm">{params.data?.size || params.data?.variantName}</div>
      <div className="text-fg-muted paragraph-sm">#{formatSkuId(params.data?.skuId)}</div>
    </div>
  )
}

const GATING_COLUMN_IDS = [
  ProductTableColumn.ForecastQuality,
  ProductTableColumn.GMROI,
  ProductTableColumn.InventoryTurn,
]

const defaultDisplayedColumns = [
  PurchaseOrderTableColumn.Quantity,
  ProductTableColumn.Tags,
  PurchaseOrderTableColumn.SuggestedOrder,
  PurchaseOrderTableColumn.Forecast,
  PurchaseOrderTableColumn.PreOrders,
  PurchaseOrderTableColumn.ProjectedStockEAD,
  ProductTableColumn.IncomingStock,
  PurchaseOrderTableColumn.ProjectedWeeksOnHand,
  PurchaseOrderTableColumn.ProjectedStock,
  ProductTableColumn.CostPrice,
  PurchaseOrderTableColumn.TotalPrice,
  PurchaseOrderTableColumn.VendorCurrency,
  PurchaseOrderTableColumn.PurchasePriceVC,
  PurchaseOrderTableColumn.TotalPurchasePriceVC,
  PurchaseOrderTableColumn.TotalPurchasePriceLC,
  PurchaseOrderTableColumn.SafetyStock,
  ProductTableColumn.ForecastQuality,
]

type PurchaseOrderTableV2Props = {
  items: { skuId: string; warehouseId: string; quantity: number }[]
  setItems: Function
  suggestedItems: {
    skuId: string
    warehouseId: string
    quantity: number
    rawQuantity: number
    missingStockEndOfPeriod: number
    purchasePeriodLostSales: number
  }[]
  nextOrderArrivalDate: Date
  expectedArrivalDate: Date
  addedDemand: string[]
  collectionIds?: number[]
  purchaseOrderId?: number
  vendorId?: string
  purchasePlanId?: number
  poStatus?: string | null
  tableId?: string
}

export const PurchaseOrderTableV2 = (props: PurchaseOrderTableV2Props) => {
  const gridRef = useRef<AgGridReact>(null)

  return (
    <SelectedVariantsProvider<RowDataType> gridRef={gridRef}>
      <PurchaseOrderTableV2Inner {...props} gridRef={gridRef} />
    </SelectedVariantsProvider>
  )
}

const PurchaseOrderTableV2Inner = ({
  items,
  setItems,
  suggestedItems,
  nextOrderArrivalDate,
  expectedArrivalDate,
  addedDemand,
  collectionIds,
  purchaseOrderId,
  vendorId,
  purchasePlanId,
  poStatus,
  tableId,
  gridRef,
}: PurchaseOrderTableV2Props & { gridRef: React.RefObject<AgGridReact | null> }) => {
  const resolvedTableId = tableId ?? PURCHASE_ORDER_UPDATE_TABLE_ID
  useOpenColumnChooserCommand(resolvedTableId)
  const router = useRouter()
  const { unfulfillablePreorders: unfulfillablePreordersFlag, noosVendorCurrency } = useFlags()

  // Already cached by the PO page; React Query dedupes rather than refetching.
  const { data: vendorCurrencyDetail } = api.purchaseOrder.get.useQuery(
    { purchaseOrderId: purchaseOrderId ?? 0 },
    { enabled: !!noosVendorCurrency && purchaseOrderId !== undefined },
  )

  const purchasePriceBySkuWarehouse = useMemo(() => {
    const out = new Map<string, number>()
    for (const item of vendorCurrencyDetail?.items ?? []) {
      if (item.resolvedPurchasePrice == null) continue
      out.set(`${item.skuId}::${item.warehouseId}`, item.resolvedPurchasePrice)
    }
    return out
  }, [vendorCurrencyDetail])
  const { hiddenColumnIds } = useColumnAvailability()
  const npiVariants = useNpiVariants()
  const npiVariantIds = useMemo(
    () => new Set((npiVariants ?? []).map((v) => v.variantId)),
    [npiVariants],
  )
  const [searchText, setSearchText] = useSessionStorage("PurchaseOrderTableV2-search-text", "")

  const [selectedAggregation] = useSessionStorage<AggregationMethod>(
    resolvedTableId + "aggregationMethod",
    AggregationMethod.Variant,
  )

  const disabledColumnIds = useDisabledColumnIdSet()
  const gatingColumnIds = useMemo(
    () => GATING_COLUMN_IDS.filter((colId) => !disabledColumnIds.has(colId)),
    [disabledColumnIds],
  )
  // Columns whose data is only fetched while the grid shows them.
  const shownGatingColumnIds = useShownColumnIds(
    resolvedTableId,
    gatingColumnIds,
    defaultDisplayedColumns,
  )

  const warehouseId = items.find((i) => !addedDemand.includes(i.warehouseId))?.warehouseId || ""

  const primaryItems = useMemo(() => {
    const grouped = groupBy(items, (i) => i.skuId)
    return Object.values(grouped).map((group) => {
      const totalQuantity = sum(group.map((item) => item.quantity))
      return {
        skuId: group[0]?.skuId ?? "",
        warehouseId,
        quantity: totalQuantity,
      }
    })
  }, [items, warehouseId])

  const [initialQuantities] = useState<Map<string, number>>(() => {
    const grouped = groupBy(items, (i) => i.skuId)
    return new Map(
      Object.values(grouped)
        .filter((g) => g.length > 0)
        .map((group) => [group[0]!.skuId, sum(group.map((i) => i.quantity))]),
    )
  })

  const hasQuantityChanges = useMemo(
    () =>
      initialQuantities.size > 0 &&
      primaryItems.some((i) => (initialQuantities.get(i.skuId) ?? i.quantity) !== i.quantity),
    [primaryItems, initialQuantities],
  )

  const skuIds = useMemo(() => uniq(primaryItems.map((i) => i.skuId)), [primaryItems])
  const { data: company } = api.company.getCompany.useQuery()
  const { data: warehouses } = api.warehouse.get.useQuery()
  const { data: skuMap } = api.sku.getDataForPOs.useQuery(
    { skuIds, warehouseIds: [warehouseId], vendorId },
    { enabled: skuIds.length > 0, placeholderData: keepPreviousData },
  )
  const skuVariantIds = useMemo(
    () => uniq(Array.from(skuMap?.values() ?? []).map((entry) => entry.variantId)),
    [skuMap],
  )

  // The PO covers demand for its primary warehouse plus any added-demand warehouses, so
  // shortfall must be counted across the same warehouse set as the pre-order queries below.
  const poWarehouseIds = useMemo(
    () => [warehouseId, ...addedDemand].filter(Boolean),
    [warehouseId, addedDemand],
  )
  const { preorders: stockWalkPreorders } = useUnfulfillablePreordersForWarehouses(
    unfulfillablePreordersFlag ? skuVariantIds : [],
    poWarehouseIds,
  )

  const { data: evaluationMap } = api.forecastEvaluation.forecastEvaluationMap.useQuery(undefined, {
    enabled: shownGatingColumnIds.has(ProductTableColumn.ForecastQuality),
  })
  const { data: extraDataFields } = useExtraDataFormattedKeys()
  const { data: collectionExtraDataFields } = useCollectionSkuExtraDataKeys()
  const isCarharttWip = useIsCarharttWip()
  const carharttSimulatedAvailability = useMemo(
    () => (isCarharttWip ? getCarharttSimulatedAvailabilityColumn(extraDataFields ?? []) : null),
    [isCarharttWip, extraDataFields],
  )
  const carharttAvailabilityViolation = useMemo(
    () => (isCarharttWip ? getCarharttAvailabilityViolationColumn(extraDataFields ?? []) : null),
    [isCarharttWip, extraDataFields],
  )
  const { data: extraDataColTypeMap } = useExtraDataTypeMap()
  const { data: extraDataAggregationMap } = useExtraDataAggregationMap()
  const { data: extraDataDisplayNameMap } = useExtraDataDisplayNameMap(
    (extraDataFields && extraDataFields.length > 0) ||
      (collectionExtraDataFields && collectionExtraDataFields.length > 0),
  )

  // Storage locations
  const { data: storageLocations } = api.storageLocation.get.useQuery()
  const { data: storageLocationsData } = useStorageLocationDataVariant(true)

  const storageColumns = useMemo(() => {
    if (!storageLocations?.length) return []

    return storageLocationColumns(storageLocations.filter((s) => s.active))
  }, [storageLocations])

  const { getSelectedVariants, clearSelectedVariants } = useSelectedVariantHandlers<RowDataType>()
  const { selectedVariantCount } = useSelectedVariantState()

  const selectedSkuIds = useMemo(
    () => getSelectedVariants().map((v) => v.skuId),
    [getSelectedVariants, selectedVariantCount],
  )

  const handleRemoveSelected = useCallback(() => {
    const idsToRemove = new Set(selectedSkuIds)
    setItems(items.filter((item) => !idsToRemove.has(item.skuId)))
    clearSelectedVariants()
  }, [items, selectedSkuIds, setItems, clearSelectedVariants])

  const rowSelection = useMemo<RowSelectionOptions>(() => {
    return {
      mode: "multiRow",
      groupSelects: "descendants",
      checkboxes: (params) => !params.node.footer,
      hideDisabledCheckboxes: true,
      selectAll: "filtered",
    }
  }, [])

  const constraints = useSkuConstraints(skuMap, company, skuIds)
  const variantIdBySku = useMemo(
    () => new Map(Array.from(skuMap?.entries() ?? [], ([skuId, sku]) => [skuId, sku.variantId])),
    [skuMap],
  )

  // Raise-only, per the shared rules (SUP-413): MOQ across the PO's lines, colli per line.
  const applyResolution = (resolve: ViolationResolver) => {
    const resolved = resolve(primaryItems)
    if (changedCells(primaryItems, resolved).size > 0) setItems(resolved)
  }
  const {
    index: violations,
    moqCount: moqViolationCount,
    colliCount: colliViolationCount,
    partialGroups,
    resolveMoq: handleResolveMoq,
    resolveColli: handleResolveColli,
  } = useQuantityViolations({
    cells: primaryItems,
    constraints,
    variantIdBySku,
    applyChanges: applyResolution,
  })

  // Resolve MOQ skips a short group that is only partly selected; the selection bar says so.
  const partialMoqGroupCount = useMemo(
    () => partialGroups(new Set(selectedSkuIds)).length,
    [partialGroups, selectedSkuIds],
  )
  const groupName = formatGroupVariableName(company?.secondaryMoqGroupVariable ?? "variantId")

  useLayoutEffect(() => {
    document.querySelectorAll(".ag-tooltip-active").forEach((el) => el.remove())
  }, [violations])

  const { getSkuForecastsBetweenDates } = useForecasts()
  const { data: preordersMap } = api.preOrder.getPreOrdersForPeriod.useQuery(
    {
      skuIds,
      warehouseIds: poWarehouseIds,
      startDate: expectedArrivalDate,
      endDate: nextOrderArrivalDate,
    },
    { select: (data) => data as Map<string, Map<string, number>> },
  )

  const { data: preordersBeforeEADMap } = api.preOrder.getPreOrdersForPeriod.useQuery(
    {
      skuIds,
      warehouseIds: poWarehouseIds,
      startDate: startOfTodayUTC(),
      endDate: subDays(expectedArrivalDate, 1),
    },
    { select: (data) => data as Map<string, Map<string, number>> },
  )

  const { data: preordersMapByDay } = api.preOrder.getPreOrdersForPeriod.useQuery(
    {
      skuIds,
      warehouseIds: poWarehouseIds,
      startDate: expectedArrivalDate,
      endDate: nextOrderArrivalDate,
      byDay: true,
    },
    {
      select: (data) => data as Map<string, Map<string, { date: Date; quantity: number }[]>>,
    },
  )

  const gmroiEnabled = shownGatingColumnIds.has(ProductTableColumn.GMROI)
  const inventoryTurnEnabled = shownGatingColumnIds.has(ProductTableColumn.InventoryTurn)
  const turnDataEnabled = gmroiEnabled || inventoryTurnEnabled
  const { data: skuInventoryTurnMap } = useSkuInventoryTurnData(
    skuIds,
    warehouseId,
    turnDataEnabled,
  )
  const { data: variantInventoryTurnMap } = useInventoryTurnData(turnDataEnabled)
  const { data: variantSalesSummaries } = useSalesSummaries(warehouseId, undefined, gmroiEnabled)

  const { data: skuSales } = api.sales.getSkuSalesForPeriod.useQuery(
    {
      variantIds: skuVariantIds,
      warehouseIds: poWarehouseIds,
      startDate: subWeeks(getStartOfCurrentWeekUTC(), 52),
      endDate: getStartOfCurrentWeekUTC(),
    },
    { select: (data) => data as Map<string, { date: Date; quantity: number }[]> },
  )

  const stockOutTimeItems = useMemo(() => {
    return primaryItems
      .map((i) => ({
        skuId: i.skuId,
        warehouseId: i.warehouseId,
        quantity: i.quantity,
        expectedArrivalDate: expectedArrivalDate,
      }))
      .filter((i) => i.quantity > 0)
  }, [primaryItems, expectedArrivalDate])

  const stockoutData = useStockOutTimesV2(
    skuVariantIds,
    expectedArrivalDate,
    nextOrderArrivalDate,
    stockOutTimeItems,
    undefined,
    true,
  )

  const { data: rawStockOutDetails } = useStockOutTimesDetails(skuVariantIds, expectedArrivalDate)

  // Runs over the loader rows this table already holds, so no extra query.
  const leadTimeAvailability = useMemo(
    () =>
      deriveLeadTimeAvailability(
        rawStockOutDetails ?? [],
        differenceInWeeksFloat(getStartOfCurrentWeekUTC(), startOfTodayUTC()),
      ),
    [rawStockOutDetails],
  )

  const rawDetailMap = useMemo(() => {
    const map = new Map<string, IStockOutTimesDetail>()
    rawStockOutDetails?.forEach((d) => map.set(`${d.skuId}:${d.warehouseId}`, d))
    return map
  }, [rawStockOutDetails])

  // Projected weeks on hand is compared against demand summed over `[primary, ...addedDemand]`
  // (the scope the PO orders for), so the runway has to be simulated against that same pooled
  // balance. Without added demand this is the table's own simulation, untouched.
  const coverStockOuts = useMemo(() => {
    if (!addedDemand.length || !company || !rawStockOutDetails) return stockoutData
    const pooled = poolAddedDemandWarehouses(rawStockOutDetails, (primaryWh) =>
      primaryWh === warehouseId ? addedDemand : [],
    )
    return getStockOutTimesV2(
      company.overstockThresholdDays,
      // Every row is on the primary warehouse, and the satellites' balances are already pooled
      // into it — simulating their own rows again would be work nothing reads.
      pooled.filter((detail) => detail.warehouseId === warehouseId),
      expectedArrivalDate,
      nextOrderArrivalDate,
      stockOutTimeItems,
      undefined,
      false,
      // The cover reads the simulation's own week-space stock-out times and arrival week.
      true,
    )
  }, [
    addedDemand,
    company,
    rawStockOutDetails,
    stockoutData,
    warehouseId,
    expectedArrivalDate,
    nextOrderArrivalDate,
    stockOutTimeItems,
  ])

  const suggestedOrderMap = useMemo(() => {
    const grouped = groupBy(suggestedItems, (x) => x.skuId)
    return new Map(
      Object.entries(grouped).map(([skuId, group]) => {
        const rawQuantityWithNegative = sum(group.map((item) => item.missingStockEndOfPeriod))
        const rawQuantity = sum(group.map((item) => Math.max(Math.ceil(item.rawQuantity), 0)))
        const quantity = sum(group.map((item) => item.quantity))
        const skuMoq = Math.max(skuMap?.get(skuId)?.settings?.minimumOrderQuantity ?? 1, 1)
        const moqQuantity = Math.ceil(quantity / skuMoq) * skuMoq
        const purchasePeriodLostSales = sum(group.map((item) => item.purchasePeriodLostSales ?? 0))

        return [
          skuId,
          {
            skuId,
            warehouseId,
            quantity,
            rawQuantityWithNegative,
            rawQuantity,
            moqQuantity,
            purchasePeriodLostSales,
          },
        ]
      }),
    )
  }, [suggestedItems, skuMap, warehouseId])

  const unfulfillableBySkuMap = useMemo(
    () => computeUnfulfillableBySkuBeforeDate(stockWalkPreorders, expectedArrivalDate),
    [stockWalkPreorders, expectedArrivalDate],
  )

  const groupedItems = useMemo(() => {
    const grouped = groupBy(primaryItems, (item) => item.skuId)
    return Object.entries(grouped).map(([skuId, group]) => {
      return {
        skuId,
        quantity: sumBy(group, "quantity"),
        warehouseId: warehouseId,
        entries: group,
      }
    })
  }, [primaryItems, warehouseId])

  const rowData = useMemo(() => {
    const day = getStartOfCurrentWeekUTC()
    // Demand and in-period returns follow the suggested-order simulation, which reads the raw ETA.
    // Stock at ETA is projected to today at the earliest, so the returns before it are as well.
    const arrivalWeek = differenceInWeeksFloat(day, expectedArrivalDate)
    const nextArrivalWeek = differenceInWeeksFloat(day, nextOrderArrivalDate)
    const stockArrivalWeek = differenceInWeeksFloat(day, notBeforeTodayUTC(expectedArrivalDate))
    return filterDefined(
      groupedItems?.map((item, index) => {
        const sku = skuMap?.get(item.skuId)
        const preordersForWarehouses = preordersMap?.get(item.skuId)
        const preordersForSku = preordersForWarehouses
          ? sum(Array.from(preordersForWarehouses.values()))
          : 0

        const preordersBeforeEADForWarehouses = preordersBeforeEADMap?.get(item.skuId)
        const preordersBeforeEADForSku = preordersBeforeEADForWarehouses
          ? sum(Array.from(preordersBeforeEADForWarehouses.values()))
          : 0

        const salesLastTwelveMonths = skuSales?.get(item.skuId)
        const skuInventoryTurn = skuInventoryTurnMap?.get(item.skuId)

        if (sku && company) {
          return {
            ...sku,
            id: item.skuId,
            skuId: item.skuId,
            quantity: item.quantity,
            item: item,
            index: index,
            // Sum the per-(sku, warehouse) demand directly from getStockOutTimesDetails — the
            // same forecast array calculateSuggestedOrder reads — so this column tracks the
            // same data SO does, avoiding the precision drift between useForecasts (raw
            // forecast × 4-decimal weight) and SO (3-decimal-rounded forecast × full-precision
            // weight).
            // Kept as a raw float per leaf; the variant group's default `sum` aggFunc sums
            // floats and the formatter rounds for display, so the displayed group total
            // matches the user-published Demand Plan value (a 1500-unit published forecast
            // would otherwise drift up to 1503 from per-cell Math.ceil of weighted fractions).
            forecastsPurchasePeriod: (() => {
              let total = 0
              for (const wh of [item.warehouseId, ...addedDemand]) {
                const entry = rawDetailMap.get(`${item.skuId}:${wh}`)
                if (!entry) continue
                total += getForecastBetweenWeeks(entry.forecasts, arrivalWeek, nextArrivalWeek)
              }
              return total
            })(),
            // Sum of forecasted returns arriving strictly after ETA and on/before
            // the period end — matches the `weekDiff > arrivalWeek && <= nextArrivalWeek`
            // convention used in `calculateSuggestedOrder` for in-period incoming.
            expectedReturnsInPeriod: (() => {
              let total = 0
              for (const wh of [item.warehouseId, ...addedDemand]) {
                const entry = rawDetailMap.get(`${item.skuId}:${wh}`)
                total += getForecastedReturnsBetweenWeeks(
                  entry?.forecastedReturns,
                  arrivalWeek,
                  nextArrivalWeek,
                )
              }
              return total
            })(),
            expectedReturnsBeforeEAD: getForecastedReturnsBetweenWeeks(
              rawDetailMap.get(`${item.skuId}:${item.warehouseId}`)?.forecastedReturns,
              -Infinity,
              stockArrivalWeek,
            ),
            forecastTwelveMonths: getSkuForecastsBetweenDates(
              item.skuId,
              [item.warehouseId, ...addedDemand],
              getStartOfCurrentWeekUTC(),
              addWeeks(getStartOfCurrentWeekUTC(), 52),
            ),
            salesQuantity: salesLastTwelveMonths ? sumBy(salesLastTwelveMonths, "quantity") : 0,
            preordersPurchasePeriod: preordersForSku,
            preordersBeforeEAD: preordersBeforeEADForSku,
            unfulfillableBeforeEAD: unfulfillableBySkuMap.get(item.skuId)?.unfulfillableQty ?? 0,
            atRiskBeforeEAD: unfulfillableBySkuMap.get(item.skuId)?.atRiskQty ?? 0,
            warehouseId: item.warehouseId,
            suggestedOrderQuantity: suggestedOrderMap.get(item.skuId)?.quantity ?? 0,
            suggestedOrderRawQuantityWithNegative:
              suggestedOrderMap.get(item.skuId)?.rawQuantityWithNegative ?? 0,
            suggestedOrderRawQuantity: suggestedOrderMap.get(item.skuId)?.rawQuantity ?? 0,
            suggestedOrderMoqQuantity: suggestedOrderMap.get(item.skuId)?.moqQuantity ?? 0,
            outOfStockInformation: stockoutData?.get(item.skuId)?.details?.[item.warehouseId],
            secondaryMoqGrouper: (
              sku[company.secondaryMoqGroupVariable as keyof POSkuData] ?? ""
            ).toString(),
            isNpi: npiVariantIds.has(sku.variantId),
            lostSalesWithinLeadTime:
              leadTimeAvailability.get(leadTimeKey(item.skuId, warehouseId))?.lostSales ?? 0,
            purchasePeriodLostSales:
              suggestedOrderMap.get(item.skuId)?.purchasePeriodLostSales ?? 0,
            // These will be enriched later with actual values from forecastedClosingStockData
            projectedStockAtArrival: 0,
            weeksInPurchasePeriod: 0,
            revenue: skuInventoryTurn?.lastYearRevenue ?? 0,
            inventoryTurnCogs: skuInventoryTurn?.cogs ?? 0,
            inventoryTurnAvgInvValue: skuInventoryTurn?.avgInvValue ?? 0,
            inventoryTurnStartDate: skuInventoryTurn?.lookbackStartDate ?? null,
          }
        }

        return undefined
      }),
    )
  }, [
    groupedItems,
    stockoutData,
    skuMap,
    preordersMap,
    preordersBeforeEADMap,
    suggestedOrderMap,
    company,
    addedDemand,
    expectedArrivalDate,
    nextOrderArrivalDate,
    getSkuForecastsBetweenDates,
    rawDetailMap,
    leadTimeAvailability,
    warehouseId,
    npiVariantIds,
    skuSales,
    unfulfillableBySkuMap,
    skuInventoryTurnMap,
  ])

  const filteredRowData = useMemo(() => {
    if (!searchText) {
      return rowData
    }
    return filterOnSearchString(rowData, searchText)
  }, [rowData, searchText])

  const mappedVariantIds = useMemo(
    () => filteredRowData.map((sku) => sku.variantId),
    [filteredRowData],
  )
  const variantIds = uniq(mappedVariantIds)
  const { data: forecastedClosingStockData } =
    api.closingStock.getForecastedClosingStockForDays.useQuery(
      {
        variantIds,
        warehouseId,
        // An overdue PO's arrival is in the past, where the projection has nothing to say.
        dates: [notBeforeTodayUTC(expectedArrivalDate)],
        aggregationKey: "skuId",
        replenishmentArrivalDate: notBeforeTodayUTC(expectedArrivalDate),
      },
      {
        enabled: variantIds.length > 0,
        select: (data) => data as Record<string, number[]>,
      },
    )

  // Calculate weeks in purchase period (from expected arrival to next order arrival)
  const weeksInPurchasePeriod = useMemo(
    () => Math.max(differenceInWeeksFloat(expectedArrivalDate, nextOrderArrivalDate), 1),
    [expectedArrivalDate, nextOrderArrivalDate],
  )

  // Demand behind the projected-cover cards, on the same `[primary, ...addedDemand]` scope the
  // runway is simulated against. Only hover cards call it, so reading the forecast curve stays
  // off the per-row render path.
  const skuDemandForWeeks = useCallback(
    (skuIds: string[], weeks: number) => {
      const until = addWeeks(expectedArrivalDate, weeks)
      const warehouses = [warehouseId, ...addedDemand]
      let total = 0
      for (const skuId of skuIds) {
        total += getSkuForecastsBetweenDates(skuId, warehouses, expectedArrivalDate, until)
      }
      return total
    },
    [getSkuForecastsBetweenDates, warehouseId, addedDemand, expectedArrivalDate],
  )

  // Enrich row data with projected stock at arrival, weeks in purchase period, and pre-computed WoH fields
  const enrichedRowData = useMemo(() => {
    return filteredRowData.map((row) => {
      // projectedStockAtArrival is the forecasted closing stock BEFORE order quantity is added
      // This is used for balancing distribution across SKUs
      const stockBeforeOrder = forecastedClosingStockData?.[row.skuId]?.[0] ?? 0

      // Pre-compute per-SKU cover fields to avoid repeated date math in cellRenderers for leaf
      // rows. Every input comes out of the same simulation — including the arrival week the
      // stock-out times are compared against — so the gate cannot contradict the times it gates.
      // No simulated detail (still loading, or no data for the SKU) means the cover has no value
      // yet — not that the SKU has no runway.
      // Extended results carry the week-space stock-out times the cover is measured in; both
      // simulations feeding `coverStockOuts` ask for them.
      const detail = coverStockOuts?.get(row.skuId)?.details?.[row.warehouseId]
      const coverDetail = detail && isExtendedStockOutResult(detail) ? detail : undefined
      const noForecast = coverStockOuts !== undefined && detail === undefined
      const {
        weeksOnHand: avgWeeksOnHandAfterArrival,
        stockAtArrival: stockAtArrivalForCover,
        stockOutDate: stockOutDateAfterArrival,
        stockoutBeforeArrival,
      } = coverDetail
        ? calculateProjectedCover({
            stockAtArrival: coverDetail.stockLevelAtArrival,
            forecastInPeriod: row.forecastsPurchasePeriod,
            stockoutWeeks: coverDetail.stockoutWeeks,
            stockoutDates: coverDetail.stockoutDates,
            arrivalWeek: coverDetail.arrivalWeek,
          })
        : {
            weeksOnHand: null,
            stockAtArrival: 0,
            stockOutDate: undefined,
            stockoutBeforeArrival: false,
          }

      const incomingDuringPeriod = computeIncomingDuringPeriod(
        row.perWarehouse.get(row.warehouseId)?.incomingOrders ?? [],
        expectedArrivalDate,
        nextOrderArrivalDate,
      )

      const bufferType = row.settings?.bufferType ?? company?.bufferType ?? "noSafetyStock"
      const bufferValue = row.settings?.bufferValue ?? company?.bufferValue ?? 0
      let safetyStockEnd: Date | undefined
      if (bufferType === "weekCover" && bufferValue > 0) {
        safetyStockEnd = addWeeks(nextOrderArrivalDate, bufferValue)
      } else if (bufferType === "serviceLevel") {
        safetyStockEnd = row.outOfStockInformation?.safetyStockOutDate
      }
      const incomingDuringSafetyStock = getIncomingOrdersInSafetyStockPeriod(
        row.perWarehouse.get(row.warehouseId)?.incomingOrders ?? [],
        nextOrderArrivalDate,
        safetyStockEnd,
      )

      const projectedStockEOP = Math.max(
        row.quantity -
          row.suggestedOrderRawQuantityWithNegative +
          (row.outOfStockInformation?.buffer ?? 0),
        0,
      )

      return {
        ...row,
        // For display: stock + order quantity
        projectedStockAtArrival: stockBeforeOrder + row.quantity,
        // For distribution balancing: stock before order
        projectedStockAtArrivalBeforeOrder: stockBeforeOrder,
        weeksInPurchasePeriod,
        avgWeeksOnHandAfterArrival,
        coverLoaded: coverDetail !== undefined || noForecast,
        noForecast,
        stockAtArrivalForCover,
        stockoutBeforeArrival,
        stockOutDateAfterArrival,
        incomingDuringPeriod,
        incomingDuringSafetyStock,
        projectedStockEOP,
      }
    })
  }, [
    filteredRowData,
    forecastedClosingStockData,
    weeksInPurchasePeriod,
    expectedArrivalDate,
    nextOrderArrivalDate,
    coverStockOuts,
    company,
  ])

  const enrichedRowDataWithStorageLocations = useMemo(() => {
    if (!enrichedRowData || !storageLocationsData) return enrichedRowData

    return enrichedRowData.map((row) => {
      const variantKey = row.variantId
      const perLocationData = storageLocationsData.get(variantKey) ?? []

      return {
        ...row,
        storageLocationData: perLocationData.map((entry) => ({
          locationId: entry.locationId,
          stockLevel: entry.skuData?.find((s) => s.skuId === row.skuId)?.quantity ?? 0,
        })),
      }
    })
  }, [enrichedRowData, storageLocationsData])

  const { data: comments } = api.comment.getByVariantIds.useQuery(
    { variantIds: variantIds },
    { enabled: variantIds.length > 0 },
  )

  const renderGroupCount = (params: ICellRendererParams<RowDataType>, label: string) => {
    if (
      params.node?.group &&
      params.node.allLeafChildren &&
      params.node.allLeafChildren.length > 1
    ) {
      const entries = params.node.allLeafChildren?.map((node: any) => node.data) || []
      return `${entries.length} ${label}s`
    }
    return null
  }

  const tableContext = useMemo(() => ({ violations, comments }), [violations, comments])

  useEffect(() => {
    gridRef.current?.api?.setGridOption("context", tableContext)
  }, [tableContext])

  const getEntries = useCallback((node: any): RowDataType[] => {
    if (node.group) {
      const leafNodes = node.allLeafChildren ?? []
      return leafNodes.map((n: any) => n.data).filter(Boolean) as RowDataType[]
    }
    return node.data ? [node.data] : []
  }, [])

  const extraDataColumns = useMemo((): ColDef<RowDataType>[] => {
    return dynamicTableColumnsV2(
      extraDataFields ?? [],
      collectionExtraDataFields ?? [],
      extraDataDisplayNameMap,
      extraDataColTypeMap,
      extraDataAggregationMap,
      collectionIds,
    ).map((colDef) => {
      return {
        ...colDef,
      } as ColDef<RowDataType>
    })
  }, [
    extraDataFields,
    collectionExtraDataFields,
    extraDataColTypeMap,
    extraDataAggregationMap,
    extraDataDisplayNameMap,
    collectionIds,
  ])

  const allExtraColumns = useMemo(() => {
    return [
      ...extraDataColumns,
      ...storageColumns,
      ...(carharttSimulatedAvailability ? [carharttSimulatedAvailability.colDef] : []),
      ...(carharttAvailabilityViolation ? [carharttAvailabilityViolation.colDef] : []),
    ]
  }, [
    extraDataColumns,
    storageColumns,
    carharttSimulatedAvailability,
    carharttAvailabilityViolation,
  ])

  const hasUnfulfillableBeforeEAD = rowData.some((sku) => sku.unfulfillableBeforeEAD > 0)
  const hasAtRiskBeforeEAD = rowData.some((sku) => sku.atRiskBeforeEAD > 0)
  // Does any SKU report Available-to-Sell? Gates the month-cover "missing ATS" warning so it
  // never fires for tenants that don't use ATS at all.
  const atsAvailable = rowData.some((sku) =>
    Array.from(sku.perWarehouse.values()).some((w) => w.availableToSell != null),
  )

  const violationsRef = useRef(violations)
  useLayoutEffect(() => {
    violationsRef.current = violations
  }, [violations])

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const colDefs = useMemo((): ColDef<RowDataType>[] => {
    const monthCoverCtx = {
      warehouseId,
      companyTotalLeadTimeDays: company?.totalLeadTimeDays,
      expectedArrivalDate,
      atsAvailable,
    }
    const baseColumns: ColDef<RowDataType>[] = [
      createStringColumn({
        colId: PurchaseOrderTableColumn.Grouping,
        field: "variantId",
        // Hidden rowGroup column: AG Grid couples the "Variant" auto-group sort to it, so it orders
        // the variant groups. Sort by product name (sorting groups by a leaf's size is meaningless).
        sortingField: "productName",
        sortingType: "string",
        headerName: "Variant",
        rowGroup: selectedAggregation === AggregationMethod.Variant,
        hide: true,
        aggFunc: "unique",
        valueGetter: (params) => {
          if (!params.data) return ""
          return formatVariantId(params.data.variantId)
        },
      }),

      createStringColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.SecondaryMoqGrouper,
        lockVisible: true,
        field: "secondaryMoqGrouper",
        headerName:
          { variantId: "Variant" }[company?.secondaryMoqGroupVariable ?? "variantId"] ??
          "Secondary MOQ Group",
        hide: selectedAggregation === AggregationMethod.Variant,
        valueGetter: (params) => {
          if (!params.data) return ""
          if (company?.secondaryMoqGroupVariable === "variantId") {
            return formatVariantId(params.data.variantId)
          } else {
            return (params.data as any)[company?.secondaryMoqGroupVariable ?? "skuId"] ?? ""
          }
        },
      }),
      createUniqueStringColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.SKU,
        lockVisible: true,
        field: "skuId",
        headerName: "SKU",
        // Sort by the shown skuId, not garment size (no sortingField → default string sort).
        cellRenderer: (params: ICellRendererParams<RowDataType>) => {
          const groupCount = renderGroupCount(params, "SKU")
          if (groupCount) return groupCount

          const skuId = params.data?.skuId ?? first(params.value)
          if (!skuId) return null
          return formatSkuId(skuId)
        },
        valueFormatter: (params) => uniqueValueFormatter(params, "SKUs"),
        tooltipTitle: "SKUs",
        uniqueSuffix: "SKUs",
        isIdentifier: true,
      }),

      // Right after SKU on a fresh or reset state: its colDef slot. A view or a user move that put it
      // elsewhere is kept as the grid has it.
      createStringColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.SizeSplit,
        lockVisible: true,
        headerName: "Size Split",
        initialWidth: 110,
        // Sort numerically by the weight; the displayed value is a "12.5%" string (sorts lexically).
        sortingField: "sizeSplitWeight",
        sortingType: "number",
        valueGetter: (params) => {
          if (params.node?.group) return ""
          const weight = params.data?.sizeSplitWeight
          if (weight == null) return ""
          return `${round(weight * 100, 1)}%`
        },
        tooltip: (params: any) => {
          if (params.node?.group) return undefined
          const fraction = params.data?.skuStockoutImputedFraction
          if (!fraction) return undefined
          return `${Math.round(fraction * 100)}% of sales is estimated due to past stockouts`
        },
        cellRenderer: (params: any) => {
          if (params.node?.group) return null
          const weight = params.data?.sizeSplitWeight
          const fraction = params.data?.skuStockoutImputedFraction
          const hasWarning = fraction != null && fraction > IMPUTATION_WARNING_THRESHOLD
          if (weight == null) return <span className="text-fg-muted">-</span>
          return (
            <div className="flex items-center gap-1">
              <span>{round(weight * 100, 1)}%</span>
              {hasWarning && (
                <Warning size={12} weight="fill" className="text-yellow-400 shrink-0" />
              )}
            </div>
          )
        },
      }),
      createUniqueStringColumn<RowDataType>({
        colId: ProductTableColumn.VariantId,
        field: "variantId",
        headerName: "Variant ID",
        isIdentifier: true,
        valueFormatter: (params) => uniqueValueFormatter(params, "Variant IDs"),
        cellRenderer: (params: ICellRendererParams<RowDataType>) => {
          const uniqueValues = uniq(
            params.node.allLeafChildren?.map((node: any) => node.data?.variantId) ?? [],
          )
          if (uniqueValues.length > 1) {
            return `${uniqueValues.length} Variant IDs`
          }
          const variantId = params.data?.variantId ?? first(params.value)
          if (!variantId) return null
          return formatVariantId(variantId)
        },
        tooltipTitle: "Variant IDs",
        uniqueSuffix: "Variant IDs",
      }),
      createStringColumn<RowDataType>({
        colId: ProductTableColumn.SupplierSku,
        field: "supplierSku",
        headerName: "Supplier SKU",
        hide: Array.from(skuMap?.values() ?? []).every(
          (sku) => !sku.supplierSku || sku.supplierSku === sku.skuId,
        ),
      }),
      createUniqueStringColumn<RowDataType>({
        colId: ProductTableColumn.ProductName,
        field: "productName",
        headerName: "Product",
        valueFormatter: (params) => uniqueValueFormatter(params, "Products"),
        tooltipTitle: "Products",
        uniqueSuffix: "Products",
      }),
      createStringColumn<RowDataType>({
        colId: ProductTableColumn.Tags,
        headerName: "Tags",
        field: "tags",
        hide: Array.from(skuMap?.values() ?? []).every((sku) => !sku.tags || sku.tags.length === 0),
        cellRenderer: (params: ICellRendererParams<RowDataType>) => {
          if (params.node?.footer) return null
          const entries = getEntries(params.node)
          const tags = uniqBy(
            entries.flatMap((entry) => entry?.tags ?? []),
            (tag) => tag.id,
          )
          if (!tags.length) return null
          // Transform RowDataType to match IVariantSummary structure for TagBadge
          const transformedEntries = entries.map((entry) => ({
            skuIds: [entry.skuId],
            productName: entry.productName,
            tags: entry.tags,
          }))
          return (
            <TagBadge
              tags={tags}
              entries={transformedEntries as IVariantSummary[]}
              count={1}
              inTable
              removable={false}
              filtreable={false}
            />
          )
        },
        tooltipComponentParams: { tooltipType: "Tags" },
        tooltip: (params) => {
          const entries = getEntries(params.node)
          const tags = uniqBy(
            entries.flatMap((entry) => entry?.tags ?? []),
            (tag) => tag.id,
          )
          // Transform to match IVariantSummary structure
          const transformedEntries = entries.map((entry) => ({
            skuIds: [entry.skuId],
            productName: entry.productName,
            tags: entry.tags,
          }))
          return { entries: transformedEntries, tags }
        },
        autoHeight: true,
        flex: 1,
        initialWidth: 175,
      }),

      createNumberColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.Quantity,
        lockVisible: true,
        field: "quantity",
        headerName: "Quantity",
        fixedWidth: 120,
        editable: (params) => !params.node?.footer && !isQuantityLockedPOStatus(poStatus),
        cellEditor: "agNumberCellEditor",
        cellEditorParams: { precision: 0, min: 0 },
        groupRowValueSetter: () => true,
        cellRenderer: (params: CustomCellRendererProps) => {
          if (params.node?.footer) {
            return <span className="paragraph-md">{formatNumber(params.value ?? 0)}</span>
          }

          const entries: RowDataType[] = params.node?.group
            ? (params.node.allLeafChildren?.map((node: any) => node.data) ?? [])
            : filterDefined([params.data])
          const lines = company
            ? violationMessages(
                violationsRef.current,
                entries.map((e) => e.skuId),
                {
                  isGroup: !!params.node?.group,
                  groupName: formatGroupVariableName(company.secondaryMoqGroupVariable),
                },
              )
            : { moq: [], colli: [], suggestedActions: [] }
          const hasViolation = lines.moq.length > 0
          const hasMinorViolation = lines.colli.length > 0
          const moqLines = lines.moq
          const colliSectionLines = [...lines.colli, ...lines.suggestedActions]
          let ssLines: string[] = []

          if (params.node?.group) {
            const allIncoming = entries.flatMap(
              (e: RowDataType) => e.incomingDuringSafetyStock ?? [],
            )
            const { warnings: ssW, suggestedActions: ssA } =
              getIncomingSafetyStockWarnings(allIncoming)
            ssLines = [...ssW, ...ssA]
          } else if (params.data) {
            const { warnings: ssW, suggestedActions: ssA } = getIncomingSafetyStockWarnings(
              params.data.incomingDuringSafetyStock,
            )
            ssLines = [...ssW, ...ssA]
          }

          const hasSafetyStockWarning = ssLines.length > 0
          const sections: { title: string; lines: string[] }[] = []
          if (moqLines.length > 0) sections.push({ title: "MOQ violations", lines: moqLines })
          if (colliSectionLines.length > 0)
            sections.push({
              title: hasMinorViolation ? "Colli size violations" : "Suggested actions",
              lines: colliSectionLines,
            })
          if (ssLines.length > 0)
            sections.push({ title: "Incoming during safety stock", lines: ssLines })

          const tooltipBody =
            sections.length > 0 ? (
              <div>
                {sections.map((section, i) => (
                  <div key={section.title}>
                    <div
                      className={cn(
                        "flex items-center h-11 border-b border-base px-4 py-3",
                        i > 0 && "border-t border-base",
                      )}
                    >
                      <h3 className="label-base">{section.title}</h3>
                    </div>
                    <div className="px-4 py-3 text-fg-muted paragraph-sm space-y-1">
                      {section.lines.map((line, j) => (
                        <p key={j}>{line}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : undefined

          const content = (
            <div className="flex items-center gap-1 h-full">
              <QuantityDiffCell
                value={params.value ?? 0}
                canEdit={false}
                alwaysShowBorder
                hasViolation={hasViolation}
                hasMinorViolation={hasMinorViolation}
                widthClassName="w-18"
                className="shrink-0 overflow-hidden focus:bg-base"
              />
              {hasSafetyStockWarning && (
                <WarningCircle
                  size={14}
                  weight="fill"
                  className="text-amber-500 shrink-0 mt-[3px]"
                />
              )}
            </div>
          )

          if (!tooltipBody) return content
          return (
            <Tooltip body={tooltipBody} className="p-0 w-[320px] max-w-[320px] overflow-hidden">
              {content}
            </Tooltip>
          )
        },
        hasTooltip: false,
      }),
      createNumberColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.SuggestedOrder,
        field: "suggestedOrderQuantity",
        // This table renders the cell as `raw (adjusted)` (see `formatSuggestedOrder`), so the
        // registry's unconstrained-only wording would be wrong here. Header name still comes from
        // the registry.
        headerTooltip:
          "The quantity to order: the unconstrained need from demand and safety stock, followed in parentheses by the quantity after minimum order and colli size requirements when they differ.",
        hasTooltip: false,
        context: {
          relationalFormula: {
            kind: "diff",
            from: {
              kind: "sum",
              terms: [
                col(PurchaseOrderTableColumn.Forecast),
                col(PurchaseOrderTableColumn.SafetyStock),
              ],
            },
            minus: [
              col(PurchaseOrderTableColumn.ProjectedStockEAD),
              col(ProductTableColumn.IncomingStock),
              ...(company?.returnsSettings?.enabled
                ? [col(ProductTableColumn.ExpectedReturns)]
                : []),
            ],
          },
          relationalFormulaNote: ({ row, result }) => {
            const lostSales = Math.ceil(Number(row.purchasePeriodLostSales ?? 0))
            const final = Number(row.suggestedOrderQuantity ?? 0)
            const lostSalesBound = lostSales > result && lostSales > 0
            const afterLostSales = lostSalesBound ? lostSales : result

            const lines: string[] = []
            if (lostSalesBound) {
              lines.push(
                `Increased to ${formatNumber(lostSales)} units to cover lost sales between ETA and incoming orders.`,
              )
            }
            if (afterLostSales !== final) {
              lines.push(
                `Adjusted to ${formatNumber(final)} units to meet minimum order and colli size requirements.`,
              )
            }
            return lines.length > 0 ? lines.join("\n") : null
          },
        } satisfies RelationalFormulaContext,
        valueFormatter: (params) => {
          const entries = getEntries(params.node)
          if (!entries.length) return "-"

          const totalRawOrder = Math.ceil(sumBy(entries, "suggestedOrderRawQuantity"))
          const totalSuggestedOrder = sumBy(entries, "suggestedOrderQuantity")
          return formatSuggestedOrder(totalRawOrder, totalSuggestedOrder)
        },
        tooltip: (params) => {
          const entries = getEntries(params.node)
          if (!entries.length) return undefined

          const totalRawOrder = Math.ceil(sumBy(entries, "suggestedOrderRawQuantity"))
          const totalSuggestedOrder = sumBy(entries, "suggestedOrderQuantity")

          if (totalRawOrder === totalSuggestedOrder) return undefined

          return `Suggestion: ${totalRawOrder} units. Adjusted to ${totalSuggestedOrder} units to meet minimum order and colli size requirements.`
        },
      }),
      createUniqueNumberColumn<RowDataType>({
        colId: ProductTableColumn.ColliSize,
        field: "settings.colliSize",
        headerName: "Colli size",
        hide: !Array.from(skuMap?.values() ?? []).some(
          (sku) => (sku.settings?.colliSize ?? company?.colliSize ?? 0) > 1,
        ),
        valueGetter: (params) => params.data?.settings?.colliSize ?? company?.colliSize,
        suffix: "units",
        tooltip: (params) => {
          const entries = getEntries(params.node)
          if (!entries.length) return undefined

          const ctxViolations = violationsRef.current

          if (params.node?.group) {
            const violationCount = entries.filter(
              (entry) => ctxViolations && hasColliViolation(ctxViolations, [entry.skuId]),
            ).length
            if (violationCount === 0) return undefined
            return `${violationCount} SKUs have colli size violations in this variant`
          }

          const entry = entries[0]
          if (!entry) return undefined

          const entryColliLines = ctxViolations
            ? colliLines(ctxViolations, [entry.skuId], { isGroup: false })
            : []
          if (entryColliLines.length > 0) return entryColliLines.join(" ")

          return company?.colliSize == entry.settings?.colliSize
            ? "Company default"
            : "SKU specific"
        },
        cellStyle: (params) => {
          const entries = getEntries(params.node)
          const ctxViolations = violationsRef.current
          const hasViolations =
            ctxViolations &&
            hasColliViolation(
              ctxViolations,
              entries.map((entry) => entry.skuId),
            )

          if (hasViolations) {
            return { color: "var(--color-warning)" }
          }
          return undefined
        },
      }),
      createUniqueNumberColumn<RowDataType>({
        colId: ProductTableColumn.MinimumOrderQuantity,
        field: "settings.minimumOrderQuantity",
        headerName: "SKU MOQ",
        hide: !Array.from(skuMap?.values() ?? []).some(
          (sku) => (sku.settings?.minimumOrderQuantity ?? 0) > 1,
        ),
        valueGetter: (params) => params.data?.settings?.minimumOrderQuantity,
        tooltip: (params) => {
          const entries = getEntries(params.node)
          if (!entries.length) return undefined

          const ctxViolations = violationsRef.current
          const hasViolations =
            ctxViolations && entries.some((entry) => ctxViolations.skuMoq.has(entry.skuId))
          if (!hasViolations) return undefined

          if (params.node?.group) {
            const violationCount = entries.filter((entry) =>
              ctxViolations?.skuMoq.has(entry.skuId),
            ).length
            return `${violationCount} SKUs have orders below the minimum order quantity`
          }

          const firstEntry = entries[0]
          if (!firstEntry) return undefined

          const violation = ctxViolations.skuMoq.get(firstEntry.skuId)
          return `Minimum order not met: ${violation?.amount} units ordered across warehouses, minimum is ${violation?.moq} units`
        },
        cellStyle: (params) => {
          const entries = getEntries(params.node)
          const ctxViolations = violationsRef.current
          const hasViolations =
            ctxViolations && entries.some((entry) => ctxViolations.skuMoq.has(entry.skuId))

          if (hasViolations) {
            return { color: "var(--color-warning)" }
          }
          return undefined
        },
      }),
      createNumberColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.PreOrdersBeforeEAD,
        field: "preordersBeforeEAD",
        headerName: formatName("preorder", true, true),
        headerTooltip: `${getColumnNaming(PurchaseOrderTableColumn.PreOrdersBeforeEAD)?.tooltip} ETA: ${formatDate(expectedArrivalDate)}.`,
        filter: true,
        initialWidth: 120,
      }),
      createNumberColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.UnfulfillableBeforeEAD,
        field: "unfulfillableBeforeEAD",
        headerSubtitle: `before ${formatDate(expectedArrivalDate, DateFormatType.MonthDay)}`,
        headerTooltip: `${getColumnNaming(PurchaseOrderTableColumn.UnfulfillableBeforeEAD)?.tooltip} ETA: ${formatDate(expectedArrivalDate)}.`,
        filter: true,
        initialWidth: 130,
        hide: !hasUnfulfillableBeforeEAD,
        cellStyle: (params) =>
          (params.value ?? 0) > 0 ? { color: "var(--color-icon-red)" } : undefined,
      }),
      createNumberColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.AtRiskBeforeEAD,
        field: "atRiskBeforeEAD",
        headerSubtitle: `before ${formatDate(expectedArrivalDate, DateFormatType.MonthDay)}`,
        headerTooltip: `${getColumnNaming(PurchaseOrderTableColumn.AtRiskBeforeEAD)?.tooltip} ETA: ${formatDate(expectedArrivalDate)}.`,
        filter: true,
        initialWidth: 130,
        hide: !hasAtRiskBeforeEAD,
        cellStyle: (params) =>
          (params.value ?? 0) > 0 ? { color: "var(--color-icon-orange)" } : undefined,
      }),
      createNumberColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.SecondaryMOQ,
        field: "settings.secondaryMoq",
        hide: !Array.from(skuMap?.values() ?? []).some(
          (sku) => (sku.settings?.secondaryMoq ?? 0) > 1,
        ),
        valueGetter: (params) => params.data?.settings?.secondaryMoq,
        // Sort groups by their smallest MOQ, not the default sum (the cell shows one MOQ or "Mixed").
        comparator: (_a, _b, nodeA, nodeB, isDescending) => {
          const resolve = (node: typeof nodeA): number | null => {
            const values = (node.group ? (node.allLeafChildren ?? []) : [node]).map(
              (n) => n.data?.settings?.secondaryMoq,
            )
            const nums = values.filter((v): v is number => typeof v === "number")
            return nums.length ? Math.min(...nums) : null
          }
          return compareWithNullsLast(resolve(nodeA), resolve(nodeB), isDescending, (a, b) => a - b)
        },
        hasTooltip: false,
        valueFormatter: (params) => {
          const entries = getEntries(params.node)
          if (!entries.length) return ""

          const uniqueSecondaryMoqs = uniq(entries.map((entry) => entry.settings?.secondaryMoq))
          const firstMoq = uniqueSecondaryMoqs[0]
          return uniqueSecondaryMoqs.length === 1 && typeof firstMoq === "number"
            ? formatNumber(firstMoq)
            : "Mixed"
        },
        cellStyle: (params) => {
          const entries = getEntries(params.node)
          const ctxViolations = violationsRef.current
          if (!entries.length || !company) return undefined

          const firstEntry = entries[0]
          if (!firstEntry) return undefined

          const violationKey = (
            firstEntry[company.secondaryMoqGroupVariable as keyof RowDataType] ?? ""
          ).toString()
          const hasViolation = !!(violationKey && ctxViolations?.secondaryMoq.has(violationKey))

          if (hasViolation) {
            return { color: "var(--color-warning)" }
          }
          return undefined
        },
      }),
      createNumberColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.ProjectedStockEAD,
        headerName: "Projected stock",
        headerSubtitle: "ETA",
        hasTooltip: false,
        filter: true,
        valueGetter: (params) => {
          if (!params.data) return
          return params.data.projectedStockAtArrivalBeforeOrder ?? 0
        },
        // The hover reads inputs beyond the value; row updates only redraw cells whose value changed.
        equals: () => false,
        cellRenderer: (params: ICellRendererParams<RowDataType>) => {
          if (!params.node.group) {
            const entry = params.node.data
            if (!entry) return null
            const entryWarehouseData = entry.perWarehouse.get(entry.warehouseId)
            const demandBeforeEAD =
              (entryWarehouseData?.stockLevel ?? 0) +
              (entryWarehouseData?.incomingStock ?? 0) +
              entry.expectedReturnsBeforeEAD -
              (entry.projectedStockAtArrivalBeforeOrder ?? 0)
            return (
              <ProjectedStockEADField
                projectedStock={entry.projectedStockAtArrivalBeforeOrder ?? 0}
                expectedArrivalDate={expectedArrivalDate}
                currentStockLevel={entryWarehouseData?.stockLevel ?? 0}
                incomingStock={entryWarehouseData?.incomingStock ?? 0}
                expectedReturns={entry.expectedReturnsBeforeEAD}
                demandInPeriod={demandBeforeEAD}
                isAggregated={false}
              />
            )
          }

          const leafNodes = params.node.allLeafChildren ?? []
          if (!leafNodes.length) return null

          const totalProjectedStock = sumBy(
            leafNodes,
            (n) => n.data?.projectedStockAtArrivalBeforeOrder ?? 0,
          )
          const totalCurrentStock = sumBy(
            leafNodes,
            (n) => n.data?.perWarehouse.get(n.data.warehouseId)?.stockLevel ?? 0,
          )
          const totalIncomingStock = sumBy(
            leafNodes,
            (n) => n.data?.perWarehouse.get(n.data.warehouseId)?.incomingStock ?? 0,
          )
          const totalReturns = sumBy(leafNodes, (n) => n.data?.expectedReturnsBeforeEAD ?? 0)
          const totalDemandBeforeEAD =
            totalCurrentStock + totalIncomingStock + totalReturns - totalProjectedStock

          return (
            <ProjectedStockEADField
              projectedStock={totalProjectedStock}
              expectedArrivalDate={expectedArrivalDate}
              currentStockLevel={totalCurrentStock}
              incomingStock={totalIncomingStock}
              expectedReturns={totalReturns}
              demandInPeriod={totalDemandBeforeEAD}
              isAggregated={true}
            />
          )
        },
        initialWidth: 175,
      }),
      createNumberColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.IncomingStockBeforeEAD,
        headerName: "Incoming stock",
        headerSubtitle: "Before ETA",
        minWidth: 140,
        hasTooltip: false,
        filter: true,
        valueGetter: (params) => {
          const entries = getEntries(params.node)
          if (!entries.length) return null
          const allIncomingOrders = aggregateIncomingOrders(
            flatten(entries.map((e) => e.perWarehouse.get(e.warehouseId)?.incomingOrders ?? [])),
          )
          const beforeEADOrders = allIncomingOrders.filter((order) =>
            isBefore(order.expectedArrivalDate, expectedArrivalDate),
          )
          return {
            value: sumBy(beforeEADOrders, "quantity"),
            incomingOrders: beforeEADOrders,
            allOrdersCount: allIncomingOrders.length,
          }
        },
        cellStyle: {
          padding: "0px 0px 0px 8px",
          margin: "0px",
        },
        cellRenderer: IncomingOrderCellRenderer,
        cellRendererParams: {
          align: "left",
          bucketLabel: `Before ETA (before ${formatDate(expectedArrivalDate, DateFormatType.FullDateShort)})`,
        },
        aggFunc: (params) => {
          return {
            value: sumBy(params.values, "value"),
            incomingOrders: aggregateIncomingOrders(
              flatten(params.values.map((v: any) => v?.incomingOrders ?? [])),
            ),
            allOrdersCount: sumBy(params.values, "allOrdersCount"),
          }
        },
        comparator: (valueA, valueB) => (valueA?.value ?? 0) - (valueB?.value ?? 0),
      }),
      createNumberColumn<RowDataType>({
        colId: ProductTableColumn.IncomingStock,
        headerName: "Incoming stock",
        headerSubtitle: "In period",
        hide: !rowData.some(
          (sku) => (sku.perWarehouse.get(sku.warehouseId)?.incomingStock ?? 0) !== 0,
        ),
        minWidth: 140,
        hasTooltip: false,
        filter: true,
        valueGetter: (params) => {
          const entries = getEntries(params.node)
          if (!entries.length) return null
          const allIncomingOrders = aggregateIncomingOrders(
            flatten(entries.map((e) => e.perWarehouse.get(e.warehouseId)?.incomingOrders ?? [])),
          )

          return {
            value: sumBy(
              allIncomingOrders.filter(
                (order) =>
                  isAfter(order.expectedArrivalDate, subDays(expectedArrivalDate, 1)) &&
                  isBefore(order.expectedArrivalDate, addDays(nextOrderArrivalDate, 1)),
              ),
              "quantity",
            ),
            incomingOrders: aggregateIncomingOrders(
              flatten(entries.map((e) => e.perWarehouse.get(e.warehouseId)?.incomingOrders ?? [])),
            ),
          }
        },
        cellStyle: {
          padding: "0px 0px 0px 8px",
          margin: "0px",
        },
        cellRenderer: IncomingOrderCellRenderer,
        cellRendererParams: {
          align: "left",
          startDate: expectedArrivalDate,
          endDate: nextOrderArrivalDate,
          bucketLabel: "In period",
        },
        aggFunc: (params) => {
          return {
            value: sumBy(params.values, "value"),
            incomingOrders: aggregateIncomingOrders(
              flatten(params.values.map((v: any) => v?.incomingOrders ?? [])),
            ),
          }
        },
        comparator: (valueA, valueB) => (valueA?.value ?? 0) - (valueB?.value ?? 0),
      }),
      createNumberColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.IncomingStockPostPeriod,
        headerName: "Incoming stock",
        headerSubtitle: "Post period",
        minWidth: 140,
        hasTooltip: false,
        filter: true,
        valueGetter: (params) => {
          const entries = getEntries(params.node)
          if (!entries.length) return null
          const allIncomingOrders = aggregateIncomingOrders(
            flatten(entries.map((e) => e.perWarehouse.get(e.warehouseId)?.incomingOrders ?? [])),
          )
          const postPeriodOrders = allIncomingOrders.filter((order) =>
            isAfter(order.expectedArrivalDate, nextOrderArrivalDate),
          )
          return {
            value: sumBy(postPeriodOrders, "quantity"),
            incomingOrders: postPeriodOrders,
            allOrdersCount: allIncomingOrders.length,
          }
        },
        cellStyle: {
          padding: "0px 0px 0px 8px",
          margin: "0px",
        },
        cellRenderer: IncomingOrderCellRenderer,
        cellRendererParams: {
          align: "left",
          bucketLabel: `Post period (after ${formatDate(nextOrderArrivalDate, DateFormatType.FullDateShort)})`,
        },
        aggFunc: (params) => {
          return {
            value: sumBy(params.values, "value"),
            incomingOrders: aggregateIncomingOrders(
              flatten(params.values.map((v: any) => v?.incomingOrders ?? [])),
            ),
            allOrdersCount: sumBy(params.values, "allOrdersCount"),
          }
        },
        comparator: (valueA, valueB) => (valueA?.value ?? 0) - (valueB?.value ?? 0),
      }),
      // Only surfaced when the company has the Returns feature enabled — matches
      // the gating used by the Collection table's Expected Returns column.
      ...(company?.returnsSettings?.enabled
        ? [
            createNumberColumn<RowDataType>({
              colId: ProductTableColumn.ExpectedReturns,
              field: "expectedReturnsInPeriod",
              headerName: "Expected Returns",
              headerSubtitle: "In period",
              filter: true,
              valueFormatter: (params) => formatNumber(params.value ?? 0),
            }),
          ]
        : []),
      createNumberColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.Forecast,
        field: "forecastsPurchasePeriod",
        headerName: "Demand",
        headerSubtitle: "in period",
        initialWidth: 120,
        valueFormatter: (params) => formatForecast(params.value),
        cellRenderer: (params: any) => {
          if (params.node?.footer) return formatForecast(params.value)

          const entries = getEntries(params.node)
          const npiEntries = entries.filter((x) => x.isNpi)

          const forecastValue = (
            <div className="flex items-center gap-1">
              {!params.value && (
                <Tooltip
                  body={
                    <div>
                      <div className="font-semibold mb-2">No demand available or 0 demand</div>
                      <div>
                        Consider adding this product to a collection, or changing to NOOS product
                        type
                      </div>
                    </div>
                  }
                >
                  <Warning className="text-warning shrink-0" />
                </Tooltip>
              )}
              {formatForecast(params.value)}
            </div>
          )

          return (
            <DemandInPeriodHoverCard
              entries={entries}
              rawDetailMap={rawDetailMap}
              addedDemand={addedDemand}
              expectedArrivalDate={expectedArrivalDate}
              nextOrderArrivalDate={nextOrderArrivalDate}
              total={params.value ?? 0}
            >
              <NpiForecastValue
                value={forecastValue}
                npiCount={npiEntries.length}
                totalCount={entries.length}
                isVariantLevel={params.node?.group}
              />
            </DemandInPeriodHoverCard>
          )
        },
        hasTooltip: false,
        filter: true,
      }),
      createNumberColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.ForecastWithoutPreOrders,
        headerName: "Forecasted reorders",
        headerSubtitle: "in period",
        initialWidth: 130,
        valueGetter: (params) => {
          if (!params.data) return 0
          return Math.max(
            0,
            params.data.forecastsPurchasePeriod - params.data.preordersPurchasePeriod,
          )
        },
        valueFormatter: (params) => formatForecast(params.value),
        hasTooltip: false,
        filter: true,
      }),
      createSumColumn<RowDataType>({
        colId: ProductTableColumn.Forecast,
        headerName: "Demand",
        headerSubtitle: "Next 52 weeks",
        field: "forecastTwelveMonths",
        initialWidth: 125,
        valueFormatter: (params) => formatNumber(roundForecast(params.value)),
        headerTooltip: () => {
          return (
            <div>
              <h3 className="bold">
                The estimated quantity expected to sell during the forecasting period.
              </h3>
              <p className="text-sm">Next 52 weeks:</p>
              <p className="text-sm">
                {`${formatDate(getStartOfCurrentWeekUTC())} - ${formatDate(addDays(getStartOfCurrentWeekUTC(), 52 * 7 - 1))}`}
              </p>
            </div>
          )
        },
        hasTooltip: false,
      }),
      createSumColumn<RowDataType>({
        colId: ProductTableColumn.Sales,
        headerName: formatName("sale", false, true),
        headerSubtitle: "Last 52 weeks",
        initialWidth: 120,
        field: "salesQuantity",
        valueFormatter: (params) => formatNumber(roundForecast(params.value)),
        headerTooltip: () => {
          return (
            <div>
              <h3 className="bold">{`Gross units sold in the ${formatName("sale")} period.`}</h3>
              <p className="text-sm">Last 52 weeks:</p>
              <p className="text-sm">
                {`${formatDate(addDays(getStartOfCurrentWeekUTC(), -(52 * 7)))} - ${formatDate(addDays(getStartOfCurrentWeekUTC(), -1))}`}
              </p>
            </div>
          )
        },
        hasTooltip: false,
      }),
      createSumColumn<RowDataType>({
        colId: ProductTableColumn.LostSales,
        field: "lostSalesWithinLeadTime",
        headerName: `Lost ${formatName("sale", true)}`,
        headerTooltip: `Unrealized gross ${formatName("sale", true)} within lead time: estimated units lost to stockouts before the next order arrives.`,
        valueFormatter: (params) => formatForecast(params.value),
        hasTooltip: false,
        filter: true,
      }),
      createSumColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.ProjectedLostSalesInPeriod,
        field: "purchasePeriodLostSales",
        headerName: `Projected lost ${formatName("sale", true)}`,
        headerSubtitle: "in period",
        headerTooltip: `Expected unrealized gross ${formatName("sale", true)} during the purchase period if nothing additional is ordered.`,
        valueFormatter: (params) => formatForecast(params.value),
        hasTooltip: false,
        filter: true,
      }),
      createSumColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.AdjustedLostSalesInPeriod,
        headerName: `Adjusted lost ${formatName("sale", true)}`,
        headerSubtitle: "in period",
        headerTooltip: `Projected lost ${formatName("sale", true)} minus the quantity on this order: remaining unrealized gross ${formatName("sale", true)} after this purchase.`,
        valueGetter: (params) => {
          if (!params.data) return 0
          return Math.max(params.data.purchasePeriodLostSales - params.data.quantity, 0)
        },
        valueFormatter: (params) => formatForecast(params.value),
        hasTooltip: false,
        filter: true,
      }),
      createAverageColumn({
        colId: ProductTableColumn.ForecastQuality,
        headerName: "Forecast Quality",
        valueGetter: (params) => {
          const variantId = params.data?.variantId
          if (!variantId) return null

          const evaluation = evaluationMap?.get(variantId)
          if (!evaluation) return null
          return evaluation.overallScore != null ? evaluation.overallScore * 100 : null
        },
        cellRenderer: QualityCellRenderer,
        cellRendererParams: {
          evaluationMap: evaluationMap,
          // Scope the lead-time panel to the warehouses this PO orders into (primary + added demand).
          warehouseIds: poWarehouseIds,
        },
        // alignment: "left",
      }),

      createNumberColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.PreOrders,
        field: "preordersPurchasePeriod",
        headerName: formatName("preorder", true, true),
        headerSubtitle: "in period",
        decimals: 1,
        initialWidth: 120,
        hasTooltip: false,
        filter: true,
        cellRenderer: (params: ICellRendererParams<RowDataType>) => {
          if (params.node?.footer) return formatNumber(params.value ?? 0)

          const entries = getEntries(params.node)
          const total = sumBy(entries, "preordersPurchasePeriod")

          return (
            <PreordersInPeriodHoverCard
              entries={entries}
              preordersMapByDay={preordersMapByDay}
              expectedArrivalDate={expectedArrivalDate}
              nextOrderArrivalDate={nextOrderArrivalDate}
              total={total}
            >
              {formatNumber(total)}
            </PreordersInPeriodHoverCard>
          )
        },
      }),
      createNumberColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.LocationMinimumStockLevel,
        headerName: "Location minimum stock",
        hasTooltip: false,
        filter: true,
        valueGetter: (params) =>
          params.data?.perWarehouse.get(warehouseId)?.locationMinimumStockLevel ?? 0,
      }),
      createNumberColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.ProjectedStock,
        headerName: "Projected stock",
        headerSubtitle: "End of purchase period",
        initialWidth: 185,
        hasTooltip: false,
        // The hover reads inputs beyond the value; row updates only redraw cells whose value changed.
        equals: () => false,
        valueGetter: (params) => {
          if (!params.data) return 0
          const { quantity, suggestedOrderRawQuantityWithNegative, outOfStockInformation } =
            params.data
          return Math.max(
            quantity - suggestedOrderRawQuantityWithNegative + (outOfStockInformation?.buffer ?? 0),
            0,
          )
        },
        cellRenderer: (params: ICellRendererParams<RowDataType>) => {
          if (!params.node.group) {
            const entry = params.node.data
            if (!entry) return null
            return (
              <ProjectedStockEOPField
                projectedStock={entry.projectedStockEOP}
                stockAtEAD={entry.projectedStockAtArrivalBeforeOrder ?? 0}
                incomingDuringPeriod={entry.incomingDuringPeriod}
                expectedReturns={entry.expectedReturnsInPeriod}
                orderQuantity={entry.quantity}
                periodDemand={entry.forecastsPurchasePeriod}
                endOfPeriodDate={nextOrderArrivalDate}
                isAggregated={false}
              />
            )
          }

          const leafNodes = params.node.allLeafChildren ?? []
          if (!leafNodes.length) return null

          const totalProjectedStock = sumBy(leafNodes, (n) => n.data?.projectedStockEOP ?? 0)
          const totalStockAtEAD = sumBy(
            leafNodes,
            (n) => n.data?.projectedStockAtArrivalBeforeOrder ?? 0,
          )
          const totalOrderQuantity = sumBy(leafNodes, (n) => n.data?.quantity ?? 0)
          const totalPeriodDemand = sumBy(leafNodes, (n) => n.data?.forecastsPurchasePeriod ?? 0)
          const totalIncoming = sumBy(leafNodes, (n) => n.data?.incomingDuringPeriod ?? 0)
          const totalReturns = sumBy(leafNodes, (n) => n.data?.expectedReturnsInPeriod ?? 0)

          return (
            <ProjectedStockEOPField
              projectedStock={totalProjectedStock}
              stockAtEAD={totalStockAtEAD}
              incomingDuringPeriod={totalIncoming}
              expectedReturns={totalReturns}
              orderQuantity={totalOrderQuantity}
              periodDemand={totalPeriodDemand}
              endOfPeriodDate={nextOrderArrivalDate}
              isAggregated={true}
            />
          )
        },
        filter: true,
      }),
      createNumberColumn<RowDataType>({
        colId: ProductTableColumn.StockLevel,
        headerName: "Stock level",
        hasTooltip: false,
        filter: true,
        valueGetter: (params) => params.data?.perWarehouse.get(warehouseId)?.stockLevel ?? 0,
        tooltip: (params) => {
          const entries = getEntries(params.node)
          if (!entries.length) return undefined

          const hasOutOfStock = entries.some(
            (entry) => entry.outOfStockInformation && entry.outOfStockInformation.stockoutDates[0],
          )
          if (!hasOutOfStock) return undefined

          if (params.node?.group) {
            const outOfStockCount = entries.filter(
              (entry) =>
                entry.outOfStockInformation && entry.outOfStockInformation.status === "stockout",
            ).length
            const willBeOutOfStockCount = entries.filter(
              (entry) =>
                entry.outOfStockInformation &&
                entry.outOfStockInformation.status !== "stockout" &&
                entry.outOfStockInformation.stockoutDates[0],
            ).length

            if (outOfStockCount > 0 && willBeOutOfStockCount > 0) {
              return `${outOfStockCount} SKUs out of stock, ${willBeOutOfStockCount} SKUs will be out of stock soon`
            } else if (outOfStockCount > 0) {
              return `${outOfStockCount} SKUs out of stock`
            } else if (willBeOutOfStockCount > 0) {
              return `${willBeOutOfStockCount} SKUs will be out of stock soon`
            }
            return undefined
          }

          const firstEntry = entries[0]
          if (!firstEntry) return undefined

          const outOfStockInformation = firstEntry.outOfStockInformation
          if (!outOfStockInformation || !outOfStockInformation.stockoutDates[0]) return undefined

          return outOfStockInformation.status === "stockout"
            ? `Out of stock for ${differenceInCalendarDaysUTC(startOfTodayUTC(), outOfStockInformation.stockoutDates[0])} days`
            : `Will be out of stock in ${differenceInCalendarDaysUTC(outOfStockInformation.stockoutDates[0], startOfTodayUTC())} days`
        },
      }),
      createNumberColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.SafetyStock,
        field: "outOfStockInformation.buffer",
        headerName: "Safety stock",
        hide: !Array.from(skuMap?.values() ?? []).some((sku) => {
          const bufferType = sku.settings?.bufferType ?? company?.bufferType
          const bufferValue = sku.settings?.bufferValue ?? 0
          const minimumStockLevel =
            sku.perWarehouse.get(warehouseId)?.locationMinimumStockLevel ?? 0
          return bufferType !== "noSafetyStock" && (bufferValue > 0 || minimumStockLevel > 0)
        }),
        valueGetter: (params) => params.data?.outOfStockInformation?.buffer ?? 0,
        tooltipComponentParams: { tooltipType: "SafetyStock" },
        tooltip: (params): SafetyStockTooltipValue | undefined => {
          const entries = getEntries(params.node)
          if (!entries.length) return undefined

          if (params.node?.group) {
            const bufferTypes = uniq(
              entries
                .map((entry) => entry.settings?.bufferType ?? company?.bufferType)
                .filter(Boolean),
            )

            if (bufferTypes.length > 1) {
              return { kind: "text", text: "Multiple safety stock types" }
            }

            const aggregatedBufferUnits = entries.reduce(
              (acc: number, e) => acc + (e.outOfStockInformation?.buffer ?? 0),
              0,
            )

            if (bufferTypes.length === 1) {
              const bufferType = bufferTypes[0]
              if (bufferType === "serviceLevel") {
                const serviceLevels = uniq(
                  entries
                    .map((entry) => entry.settings?.bufferValue ?? company?.bufferValue)
                    .filter((val) => val !== undefined),
                )
                // Aggregate sigma across SKUs: totalSigma = sum(bufferUnits_i / zScore_i)
                // Then Z_agg = totalBuffer / totalSigma (Martijn's methodology for mixed service levels)
                const leadtimeWeek = differenceInWeeksFloat(startOfTodayUTC(), expectedArrivalDate)
                const aggregatedSigma = (() => {
                  const totalSigma = entries.reduce((acc, entry) => {
                    const z = entry.settings?.bufferValue ?? company?.bufferValue ?? 0
                    const buf = entry.outOfStockInformation?.buffer ?? 0
                    return z > 0 && buf > 0 ? acc + buf / z : acc
                  }, 0)
                  return totalSigma > 0 ? totalSigma : undefined
                })()
                const zScore =
                  aggregatedSigma != null && aggregatedSigma > 0
                    ? aggregatedBufferUnits / aggregatedSigma
                    : (serviceLevels[0] ?? 2)
                const aggregatedMu = entries.reduce((acc, entry) => {
                  const detail = rawDetailMap.get(`${entry.skuId}:${entry.warehouseId}`)
                  return (
                    acc +
                    (detail && leadtimeWeek > 0
                      ? getForecastBetweenWeeks(detail.forecasts, 0, leadtimeWeek)
                      : 0)
                  )
                }, 0)
                return {
                  kind: "serviceLevel",
                  serviceLevel: zScore,
                  bufferUnits: aggregatedBufferUnits,
                  minimumStockLevel: 0,
                  sigma: aggregatedSigma,
                  mu: leadtimeWeek <= 0 ? 0 : aggregatedMu > 0 ? aggregatedMu : undefined,
                  weightedAverage: serviceLevels.length > 1,
                }
              } else if (bufferType === "weekCover") {
                const weekCovers = uniq(
                  entries
                    .map((entry) => entry.settings?.bufferValue ?? company?.bufferValue)
                    .filter((val) => val !== undefined),
                )
                if (weekCovers.length === 1) {
                  const startWeek = differenceInWeeksFloat(startOfTodayUTC(), nextOrderArrivalDate)
                  const weeks = weekCovers[0] ?? 0
                  const weeklyData = Array.from({ length: Math.ceil(weeks) }, (_, i) => {
                    const weekStart = startWeek + i
                    const weekEnd = Math.min(weekStart + 1, startWeek + weeks)
                    const forecast = entries.reduce((acc, entry) => {
                      const detail = rawDetailMap.get(`${entry.skuId}:${entry.warehouseId}`)
                      return (
                        acc +
                        (detail ? getForecastBetweenWeeks(detail.forecasts, weekStart, weekEnd) : 0)
                      )
                    }, 0)
                    return { forecast }
                  })
                  return {
                    kind: "weekCover",
                    weeks,
                    bufferUnits: aggregatedBufferUnits,
                    minimumStockLevel: 0,
                    weeklyData,
                    aggregated: true,
                  }
                }
                return { kind: "text", text: "Multiple Week Covers" }
              }
            }

            return undefined
          }

          const entry = entries[0]
          if (!entry) return undefined

          const bufferType = entry.settings?.bufferType ?? company?.bufferType
          const bufferValue = entry.settings?.bufferValue ?? company?.bufferValue
          const minimumStockLevel =
            entry.perWarehouse.get(entry.warehouseId)?.locationMinimumStockLevel ?? 0
          const bufferUnits = entry.outOfStockInformation?.buffer ?? 0

          if (!bufferType) return undefined

          if (bufferType === "noSafetyStock") {
            return { kind: "noSafetyStock" }
          }

          if (bufferType === "serviceLevel") {
            const detail = rawDetailMap.get(`${entry.skuId}:${entry.warehouseId}`)
            const leadtimeWeek = differenceInWeeksFloat(startOfTodayUTC(), expectedArrivalDate)
            const mu = detail
              ? leadtimeWeek > 0
                ? getForecastBetweenWeeks(detail.forecasts, 0, leadtimeWeek)
                : 0
              : undefined
            const zScore = bufferValue ?? 0
            const sigma = zScore > 0 && bufferUnits > 0 ? bufferUnits / zScore : undefined
            return {
              kind: "serviceLevel",
              serviceLevel: zScore,
              bufferUnits,
              minimumStockLevel,
              mu,
              sigma,
            }
          }

          if (bufferType === "weekCover") {
            const detail = rawDetailMap.get(`${entry.skuId}:${entry.warehouseId}`)
            if (!detail) {
              return {
                kind: "weekCover",
                weeks: bufferValue ?? 0,
                bufferUnits,
                minimumStockLevel,
                weeklyData: [],
              }
            }

            const startWeek = differenceInWeeksFloat(startOfTodayUTC(), nextOrderArrivalDate)
            const weeks = detail.bufferValueEffective ?? 0
            const weeklyData = Array.from({ length: Math.ceil(weeks) }, (_, i) => {
              const weekStart = startWeek + i
              const weekEnd = Math.min(weekStart + 1, startWeek + weeks)
              const forecast = getForecastBetweenWeeks(detail.forecasts, weekStart, weekEnd)
              return { forecast }
            })

            return {
              kind: "weekCover",
              weeks,
              bufferUnits,
              minimumStockLevel,
              weeklyData,
            }
          }

          return undefined
        },
      }),
      createAverageColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.ProjectedWeeksOnHand,
        headerName: "Projected weeks on hand",
        headerInfoIcon: true,
        headerTooltip: () => {
          return (
            <div>
              <h3 className="bold">How long will stock last?</h3>
              <p className="text-sm">
                Weeks of inventory remaining after this order arrives, based on forecasted demand.
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Higher = more buffer before stock-out
              </p>
            </div>
          )
        },
        decimals: 1,
        hasTooltip: false,
        // Neither non-finite (stock never depletes) nor null (no demand in the window) has a
        // numeric meaning for ag-grid's average aggregation, so both report as "no value".
        valueGetter: (params) => {
          const value = params.data?.avgWeeksOnHandAfterArrival
          return value != null && Number.isFinite(value) ? value : null
        },
        // Keeps "52+" (Infinity) out of the Blank filter. `groupAggFiltering` also tests groups (no `data`).
        filterValueGetter: (params) =>
          params.node?.group
            ? resolveGroupCover(params.node)
            : (params.data?.avgWeeksOnHandAfterArrival ?? null),
        filterParams: { buttons: ["clear"] },
        // Sort by the value the cell renders: numbers, then 52+, then no demand.
        comparator: (_a, _b, nodeA, nodeB, isDescending) =>
          compareWithNullsLast(
            resolveGroupCover(nodeA),
            resolveGroupCover(nodeB),
            isDescending,
            (a, b) => (a === b ? 0 : a - b),
          ),
        tooltip: (params) => (params.node ? noDemandTooltip(params.node) : undefined),
        cellRenderer: (params: ICellRendererParams<RowDataType>) => {
          if (isNoDemandNode(params.node)) {
            return <span className="text-fg-muted">No demand</span>
          }
          // Leaf rows: read pre-computed fields directly — avoids repeated date math on expand
          if (!params.node.group) {
            const entry = params.node.data
            if (!entry || entry.avgWeeksOnHandAfterArrival == null) return null
            return (
              <ProjectedWeeksOnHandField
                weeksOnHand={entry.avgWeeksOnHandAfterArrival}
                expectedArrivalDate={expectedArrivalDate}
                endOfPeriodDate={nextOrderArrivalDate}
                stockOutDate={entry.stockOutDateAfterArrival}
                projectedStockAtArrival={entry.stockAtArrivalForCover}
                forecastDemand={entry.forecastsPurchasePeriod}
                demandForWeeks={(weeks) => skuDemandForWeeks([entry.skuId], weeks)}
                orderQuantity={entry.quantity}
                safetyStock={entry.outOfStockInformation?.buffer ?? 0}
                isAggregated={false}
                stockoutBeforeArrival={entry.stockoutBeforeArrival}
              />
            )
          }

          // Group rows: aggregate across children
          const leafNodes = params.node.allLeafChildren ?? []
          const valuedNodes = leafNodes.filter(
            (node) => node.data?.avgWeeksOnHandAfterArrival != null,
          )
          if (!valuedNodes.length) return null

          // Average the leaves that deplete; when none of the ones with demand do, the variant's
          // cover is unbounded and renders "52+" rather than an empty cell.
          const depletingNodes = valuedNodes.filter((node) =>
            Number.isFinite(node.data?.avgWeeksOnHandAfterArrival),
          )
          const avgWeeksOnHand = depletingNodes.length
            ? meanBy(depletingNodes, (node) => node.data!.avgWeeksOnHandAfterArrival!)
            : Infinity

          const totalStockAtArrival = valuedNodes.reduce(
            (sum, node) => sum + (node.data?.stockAtArrivalForCover ?? 0),
            0,
          )
          const totalDemand = valuedNodes.reduce(
            (sum, node) => sum + (node.data?.forecastsPurchasePeriod ?? 0),
            0,
          )
          const totalOrderQuantity = valuedNodes.reduce(
            (sum, node) => sum + (node.data?.quantity ?? 0),
            0,
          )
          const totalSafetyStock = valuedNodes.reduce(
            (sum, node) => sum + (node.data?.outOfStockInformation?.buffer ?? 0),
            0,
          )
          const groupSkuIds = valuedNodes.map((node) => node.data!.skuId)

          return (
            <ProjectedWeeksOnHandField
              weeksOnHand={avgWeeksOnHand}
              expectedArrivalDate={expectedArrivalDate}
              endOfPeriodDate={nextOrderArrivalDate}
              stockOutDate={undefined}
              projectedStockAtArrival={totalStockAtArrival}
              forecastDemand={totalDemand}
              // The group's rate over the group's own window. Summing each SKU's rate would add
              // up rates measured over different windows, which is not a rate over any window.
              demandForWeeks={(weeks) => skuDemandForWeeks(groupSkuIds, weeks)}
              orderQuantity={totalOrderQuantity}
              safetyStock={totalSafetyStock}
              isAggregated={true}
              // Variants whose children run dry before the order lands must say so; a group of
              // single-SKU variants would otherwise show a bare 0 with no explanation.
              stockoutBeforeArrival={valuedNodes.some((node) => node.data?.stockoutBeforeArrival)}
            />
          )
        },
        tooltipTitle: "Projected weeks on hand from expected arrival date",
        filter: true,
        initialWidth: 175,
      }),
      createPOMonthCoverColumn(PurchaseOrderTableColumn.MonthCover, "current", monthCoverCtx),
      createPOMonthCoverColumn(PurchaseOrderTableColumn.MonthCoverWithPO, "withPO", monthCoverCtx),
      createPOMonthCoverColumn(
        PurchaseOrderTableColumn.MonthCoverWithPOMinusLeadtime,
        "withPOMinusLeadtime",
        monthCoverCtx,
      ),
      createUniqueStringColumn<RowDataType>({
        colId: ProductTableColumn.Color,
        field: "color",
        headerName: "Color",
        hide: !Array.from(skuMap?.values() ?? []).some((sku) => sku.color),
        tooltipTitle: "Colors",
      }),
      createUniqueStringColumn<RowDataType>({
        colId: ProductTableColumn.Size,
        field: "size",
        headerName: "Size",
        hide: !Array.from(skuMap?.values() ?? []).some((sku) => sku.size),
        tooltipTitle: "Sizes",
        valueFormatter: (params) => sizeValueFormatter(params),
        comparator: (valueA: string, valueB: string) => {
          return sortSizesComparator(valueA ?? "", valueB ?? "")
        },
      }),
      ...([ProductTableColumn.SizeDimension1, ProductTableColumn.SizeDimension2] as const).map(
        (colId) => ({
          ...createSizeDimensionColumn<RowDataType>({ colId }),
          hide: !Array.from(skuMap?.values() ?? []).some((sku) => sku[colId]),
        }),
      ),
      createSizeSplitConfidenceColumn<RowDataType>({
        colId: ProductTableColumn.SizeSplitConfidence,
        isHigherAggregation:
          selectedAggregation !== AggregationMethod.SKU &&
          selectedAggregation !== AggregationMethod.Variant,
        getEntries: (node) => getEntries(node),
      }),
      createAverageColumn<RowDataType>({
        colId: ProductTableColumn.CostPrice,
        field: "costPrice",
        headerName: "Cost price",
        hide: !Array.from(skuMap?.values() ?? []).some((sku) => sku.costPrice !== null),
        decimals: 1,
        suffix: company?.currency ?? "DKK",
        initialWidth: 100,
      }),
      createNumberColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.TotalPrice,
        headerName: "Total price",
        hide: !Array.from(skuMap?.values() ?? []).some((sku) => sku.costPrice !== null),
        valueGetter: (params) => (params.data?.costPrice ?? 0) * (params.data?.quantity ?? 0),
        decimals: 1,
        useFormatWithSuffix: true,
        suffix: company?.currency ?? "DKK",
        hasTooltip: false,
      }),
      // Needs a saved PO to resolve the vendor, so the create flow omits these entirely
      // rather than showing four permanently blank columns.
      ...(noosVendorCurrency && purchaseOrderId !== undefined
        ? [
            createUniqueStringColumn<RowDataType>({
              colId: PurchaseOrderTableColumn.VendorCurrency,
              headerName: "Currency (VC)",
              tooltipTitle: "Currencies",
              valueGetter: () => vendorCurrencyDetail?.currency,
              initialWidth: 100,
            }),
            // Read-only here by design: the price is negotiated and edited on the buy plan,
            // and a PO must not become a second place it can change.
            createUniqueNumberColumn<RowDataType>({
              colId: PurchaseOrderTableColumn.PurchasePriceVC,
              headerName: "Purchase price (VC)",
              tooltipTitle: "Purchase prices",
              noUniqueRange: true,
              valueGetter: (params) =>
                params.data
                  ? purchasePriceBySkuWarehouse.get(
                      `${params.data.skuId}::${params.data.warehouseId}`,
                    )
                  : undefined,
              // The inherited unique formatter returns single values raw, which renders a
              // full-precision float with a dot separator. Format them here instead.
              valueFormatter: (params) => {
                const values = Array.isArray(params.value) ? params.value : [params.value]
                const distinct = [...new Set(values.filter((v) => v != null))]
                if (distinct.length === 0) return "-"
                if (distinct.length > 1) {
                  return formatUniqueValues(distinct, "purchase prices", false, true)
                }
                return formatNumber(Number(distinct[0]), 2)
              },
              decimals: 2,
              suffix: vendorCurrencyDetail?.currency,
              initialWidth: 130,
            }),
            createNumberColumn<RowDataType>({
              colId: PurchaseOrderTableColumn.TotalPurchasePriceVC,
              headerName: "Total purchase price (VC)",
              valueGetter: (params) => {
                if (!params.data) return undefined
                const price = purchasePriceBySkuWarehouse.get(
                  `${params.data.skuId}::${params.data.warehouseId}`,
                )
                if (price == null) return undefined
                return price * (params.data.quantity ?? 0)
              },
              aggFunc: "sum",
              decimals: 1,
              useFormatWithSuffix: true,
              suffix: vendorCurrencyDetail?.currency,
              hasTooltip: false,
            }),
            createNumberColumn<RowDataType>({
              colId: PurchaseOrderTableColumn.TotalPurchasePriceLC,
              headerName: "Total purchase price (LC)",
              valueGetter: (params) => {
                const rate = vendorCurrencyDetail?.exchangeRateToLocal
                if (rate == null || !params.data) return undefined
                const price = purchasePriceBySkuWarehouse.get(
                  `${params.data.skuId}::${params.data.warehouseId}`,
                )
                if (price == null) return undefined
                return price * (params.data.quantity ?? 0) * rate
              },
              aggFunc: "sum",
              decimals: 1,
              useFormatWithSuffix: true,
              suffix: vendorCurrencyDetail?.companyCurrency ?? company?.currency ?? "DKK",
              hasTooltip: false,
            }),
          ]
        : []),
      createUniqueNumberColumn<RowDataType>({
        colId: ProductTableColumn.LeadTime,
        field: "settings.totalLeadTimeDays",
        headerName: "Total lead time",
        valueGetter: (params) =>
          params.data?.settings?.totalLeadTimeDays ?? company?.totalLeadTimeDays,
        suffix: "days",
        tooltipTitle: "Total lead time",
      }),
      createUniqueNumberColumn<RowDataType>({
        colId: ProductTableColumn.PurchaseInterval,
        field: "settings.purchaseIntervalDays",
        headerName: "Purchase interval",
        hide: !Array.from(skuMap?.values() ?? []).some(
          (sku) =>
            (sku.settings?.purchaseIntervalDays ?? company?.preferredPurchasedIntervalDays) !==
            undefined,
        ),
        valueGetter: (params) =>
          params.data?.settings?.purchaseIntervalDays ?? company?.preferredPurchasedIntervalDays,
        suffix: "days",
        tooltipTitle: "Purchase intervals",
      }),
      ...inventoryTurnColumns({ variantInventoryTurnMap, variantSalesSummaries, company }),

      createStringColumn<RowDataType>({
        colId: ProductTableColumn.ABC,
        valueGetter: (params) => {
          const p = params.data?.abcPercentile
          if (p == null) return null
          return p > 0.3 ? "A" : p > 0.1 ? "B" : "C"
        },
        aggFunc: "unique",
        cellRenderer: (params: any) =>
          params.value ? (
            <Badge className="h-[20px]" variant="systemTag">
              {params.value}
            </Badge>
          ) : (
            "-"
          ),
      }),
      createUniqueNumberColumn<RowDataType>({
        colId: ProductTableColumn.WeekCover,
        valueGetter: (params) =>
          params.data?.settings?.bufferType === "weekCover"
            ? (params.data.settings.bufferValue as number)
            : undefined,
        suffix: "weeks",
        tooltipTitle: "Week covers",
      }),

      createActionsColumn<RowDataType>({
        colId: PurchaseOrderTableColumn.Actions,
        lockVisible: true,
        numberOfActions: 2,
        cellRenderer: (params: ICellRendererParams<RowDataType>) => {
          const skuIds = params.node?.group
            ? params.node.allLeafChildren?.map((node: any) => node.data?.skuId) || []
            : params.data
              ? [params.data.skuId]
              : []

          if (!skuIds.length) return null

          // Determine rowId and label for comments
          const rowId = params.node?.group
            ? String(params.node.key ?? purchaseOrderId)
            : (params.data?.variantId ?? "")
          const rowLabel = params.node?.group
            ? (params.data?.variantName ?? String(params.node.key ?? ""))
            : (params.data?.variantName ?? params.data?.skuId ?? "")

          const additionalSources = [
            ...(collectionIds && collectionIds.length > 0
              ? [
                  {
                    documentType: DocumentType.FashionSupplyPlan,
                    documentIds: collectionIds.map(String),
                  },
                  {
                    documentType: DocumentType.FashionDemandPlan,
                    documentIds: collectionIds.map(String),
                  },
                ]
              : []),
            ...(purchasePlanId != null
              ? [{ documentType: DocumentType.BuyPlan, documentIds: [String(purchasePlanId)] }]
              : []),
          ]

          return (
            <div className={CommentsCellWrapperClassName}>
              {purchaseOrderId && (
                <DocumentCommentsButton
                  documentType={DocumentType.PurchaseOrder}
                  documentIds={[String(purchaseOrderId)]}
                  additionalSources={additionalSources.length > 0 ? additionalSources : undefined}
                  rowId={rowId}
                  rowLabel={rowLabel}
                />
              )}
              <IconButton
                variant="neutral"
                size="sm"
                onClick={() => {
                  setItems(items.filter((item) => !skuIds.includes(item.skuId)))
                }}
              >
                <TrashSimple />
              </IconButton>
            </div>
          )
        },
      }),
    ]

    return [...baseColumns, ...(allExtraColumns as ColDef<RowDataType>[])].filter(
      // Company-disabled columns are left out rather than hidden, so saved state keeps them for
      // when they are re-enabled.
      (colDef) =>
        !hiddenColumnIds.includes(colDef.colId as PurchaseOrderTableColumn) &&
        !disabledColumnIds.has(colDef.colId ?? ""),
    )
  }, [
    company,
    skuMap,
    warehouses,
    selectedAggregation,
    expectedArrivalDate,
    getEntries,
    evaluationMap,
    setItems,
    storageColumns,
    allExtraColumns,
    nextOrderArrivalDate,
    purchaseOrderId,
    purchasePlanId,
    rawDetailMap,
    preordersMapByDay,
    hasUnfulfillableBeforeEAD,
    hasAtRiskBeforeEAD,
    atsAvailable,
    variantInventoryTurnMap,
    variantSalesSummaries,
    hiddenColumnIds,
    disabledColumnIds,
    warehouseId,
    addedDemand,
    skuDemandForWeeks,
    items,
    poStatus,
  ])

  const {
    columnDefs,
    chooserProps,
    gridProps: gridOwnedColumnProps,
  } = useGridOwnedColumns(resolvedTableId, colDefs, { defaultColumnIds: defaultDisplayedColumns })

  const columnHeaderNames = useMemo(() => headerNamesByColId(colDefs), [colDefs])
  const chooserCopy = useMemo(
    () =>
      poChooserCopy([
        ...(carharttSimulatedAvailability ? [carharttSimulatedAvailability.columnEntry] : []),
        ...(carharttAvailabilityViolation ? [carharttAvailabilityViolation.columnEntry] : []),
      ]),
    [carharttSimulatedAvailability, carharttAvailabilityViolation],
  )
  useRegisterTable(resolvedTableId, {
    gridOwnedChooserProps: {
      ...chooserProps,
      fallbackColumnTitles: columnHeaderNames,
      chooser: chooserCopy,
    },
  })

  const { getGridApi, getInitialState } = useGridStateStorage(resolvedTableId)

  // Default click calls clearState() → sizeColumnsToFit(), which leaves few visible columns very wide.
  // autoSizeAllColumns() (≈ double-clicking a header edge) gives content-based widths instead.
  // getInitialState() === null uniquely identifies clearState(): saveState() always writes initialStateId
  // before firing signalGridValueChange, so null means clearState() just ran.
  useEffect(() => {
    return subscribeGridValueChange(resolvedTableId, () => {
      if (getInitialState()) return
      const api = getGridApi()
      if (api && !api.isDestroyed?.()) {
        setTimeout(() => api.autoSizeAllColumns(false), 0)
      }
    })
  }, [resolvedTableId, getInitialState, getGridApi])

  // Flash only quantity cells whose value actually changed.
  // enableCellChangeFlash can't be used here because the useMemo chain recreates all row objects
  // on every items update, causing AG Grid to flash every row regardless of whether the value changed.
  const prevQuantitiesRef = useRef<Map<string, number> | null>(null)
  useEffect(() => {
    const current = new Map(primaryItems.map((i) => [i.skuId, i.quantity]))
    const prev = prevQuantitiesRef.current
    prevQuantitiesRef.current = current
    if (!prev) return

    const changedSkuIds = primaryItems
      .filter((i) => prev.get(i.skuId) !== i.quantity)
      .map((i) => i.skuId)
    if (changedSkuIds.length === 0) return

    // AG Grid 36 mounts cell comps async; flashing in the same commit as the rowData
    // update can hit an unmounted cell and throw. The flash is cosmetic, so defer and skip.
    const timer = setTimeout(() => {
      const api = gridRef.current?.api
      if (!api || api.isDestroyed()) return
      const rowNodes = filterDefined(changedSkuIds.map((id) => api.getRowNode(id)))
      if (rowNodes.length === 0) return
      try {
        api.flashCells({
          rowNodes,
          columns: [PurchaseOrderTableColumn.Quantity],
        })
      } catch {
        // only the flash is skipped; the value change itself already landed
      }
    }, 100)
    return () => clearTimeout(timer)
  }, [primaryItems])

  // Refresh violation-dependent cells whenever violations change.
  // columnDefs no longer include violations as a dep, so ag-grid won't auto-rerender these.
  useEffect(() => {
    gridRef.current?.api?.refreshCells({
      columns: [
        PurchaseOrderTableColumn.Quantity,
        ProductTableColumn.ColliSize,
        ProductTableColumn.MinimumOrderQuantity,
        PurchaseOrderTableColumn.SecondaryMOQ,
      ],
      force: true,
    })
  }, [violations])

  const autoGroupColumnDef = useMemo((): ColDef<RowDataType> => {
    return createGroupedStringColumn<RowDataType>({
      minWidth: 300,
      headerName: "Variant",
      cellRendererSelector: () => ({
        component: "agGroupCellRenderer",
        params: {
          suppressCount: true,
          innerRenderer: (params: any) => {
            const footer = params.node.footer
            if (footer) {
              return <span className="font-semibold">Totals</span>
            }

            const group = params.node.group

            if (group) {
              const entries = filterDefined(
                params.node?.allLeafChildren?.map((n: any) => n.data),
              ) as RowDataType[]
              const firstEntry = first(entries)
              const productName = firstEntry?.productName ?? "Unknown product"
              const variantId = firstEntry?.variantId ?? "Unknown variant"
              const variantName = firstEntry?.variantName ?? "Unknown variant"
              const imageUrl = selectBestImageUrlFromObjects(entries) ?? ""
              const showBothNames =
                !variantName.includes(productName) && productName !== "Unknown product"
              const title = showBothNames ? `${productName} / ${variantName}` : variantName

              const variantComments = (params.context?.comments as typeof comments)?.filter(
                (comment) => comment.variantId === variantId,
              )
              const hasComments = variantComments && variantComments.length > 0

              const onOpen = (e: React.MouseEvent) => {
                const newTab = e.metaKey || e.ctrlKey
                const productId = firstEntry?.productId
                const variantIds = uniq(entries.map((entry) => entry.variantId))
                if (productId) openProduct(productId, variantIds, router, newTab)
              }

              return (
                <ProductCellContent
                  imageUrl={imageUrl}
                  title={title}
                  subtitle={`#${formatVariantId(variantId)}`}
                  icons={
                    hasComments
                      ? [<CommentsHoverCard key="1" comments={variantComments} />]
                      : undefined
                  }
                  onOpen={onOpen}
                />
              )
            }
            return <SkuCellRenderer {...params} />
          },
        },
      }),
    })
  }, [comments, router])

  const handleCellValueChanged = useCallback(
    (params: any) => {
      if (params.colDef.field === "quantity") {
        const newQuantity = Number(params.newRawValue ?? params.newValue) || 0

        if (params.node?.group) {
          const entries = params.node.allLeafChildren?.map((node: any) => node.data) || []

          if (newQuantity === 0) {
            const skuIdsInGroup = entries.map((entry: any) => entry.skuId)
            const updatedItems = primaryItems.map((item) =>
              skuIdsInGroup.includes(item.skuId) ? { ...item, quantity: 0 } : item,
            )
            setItems(updatedItems)
          } else {
            // Map projectedStockAtArrivalBeforeOrder + incomingDuringPeriod to projectedStockAtArrival for balancing
            const entriesForBalancing = entries.map((entry: RowDataType) => ({
              ...entry,
              projectedStockAtArrival:
                (entry.projectedStockAtArrivalBeforeOrder ?? 0) + entry.incomingDuringPeriod,
            }))
            const adjustedItems = adjustPurchaseOrder(
              newQuantity,
              entriesForBalancing,
              true,
              false,
              true,
            )
            const updatedItems = primaryItems.map((item) => {
              const adjustedItem = adjustedItems.find(
                (adj) => adj.warehouseId === item.warehouseId && adj.skuId === item.skuId,
              )
              return adjustedItem ? { ...item, quantity: adjustedItem.quantity } : item
            })
            setItems(updatedItems)
          }
          return
        }

        const skuId = params.data?.skuId
        if (!skuId) return

        const currentItem = params.data as RowDataType
        if (!currentItem) return

        if (newQuantity === 0) {
          const updatedItems = primaryItems.map((item) =>
            item.skuId === skuId ? { ...item, quantity: 0 } : item,
          )
          setItems(updatedItems)
        } else {
          // Map projectedStockAtArrivalBeforeOrder + incomingDuringPeriod to projectedStockAtArrival for balancing
          const itemForBalancing = {
            ...currentItem,
            projectedStockAtArrival:
              (currentItem.projectedStockAtArrivalBeforeOrder ?? 0) +
              currentItem.incomingDuringPeriod,
          }
          const adjustedItems = adjustPurchaseOrder(
            newQuantity,
            // @ts-expect-error
            [itemForBalancing],
            true,
            false,
            true,
          )
          const updatedItems = primaryItems.map((item) => {
            const adjustedItem = adjustedItems.find(
              (adj) => adj.warehouseId === item.warehouseId && adj.skuId === item.skuId,
            )
            return adjustedItem ? { ...item, quantity: adjustedItem.quantity } : item
          })
          setItems(updatedItems)
        }
      }
    },
    [primaryItems, setItems],
  )

  const handleSyncQuantityToPreorders = (type: "BeforeEAD" | "PurchasePeriod") => {
    setItems(syncToPreorders(primaryItems, rowData, type))
  }

  const handleSyncToUnfulfillablePreorders = (type: "unfulfillable" | "both") => {
    setItems(syncToUnfulfillablePreorders(primaryItems, rowData, type))
  }

  const resetToSuggestedOrder = () => {
    const updatedItems = primaryItems
      .filter((i) => i.warehouseId === warehouseId)
      .map((i) => {
        const sku = rowData.find((sku: any) => sku.skuId === i.skuId)
        if (sku) {
          const newQuantity = Math.ceil(largest(sku.suggestedOrderRawQuantity ?? 0, 0) ?? 0)
          return newQuantity !== i.quantity ? { ...i, quantity: newQuantity } : i
        } else {
          return i
        }
      })

    setItems(updatedItems)
  }

  const discardChanges = () => {
    if (initialQuantities.size === 0) return

    gridRef.current?.api?.forEachNode((node) => {
      if (node.group && node.data) {
        node.data.quantity = undefined
      }
    })

    const updatedItems = primaryItems.map((i) => {
      const initialQty = initialQuantities.get(i.skuId)
      if (initialQty === undefined || i.quantity === initialQty) return i
      return { ...i, quantity: initialQty }
    })

    setItems(updatedItems)
  }

  const getRowId = useMemo<GetRowIdFunc>(() => {
    return (params: GetRowIdParams) => params.data?.skuId?.toString() ?? ""
  }, [])

  // Track expanded groups in a ref so rowData rebuilds (delete/edit, which recreate the group
  // nodes) don't collapse them back to groupDefaultExpanded.
  const expandedGroupKeysRef = useRef<Set<string>>(new Set())

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      suppressHeaderMenuButton: true,
      cellRendererParams: {
        suppressCount: true,
      },
      wrapHeaderText: true,
      minWidth: 80,
      initialWidth: 110,
      tooltipComponent: CustomAgGridTooltip,
    }
  }, [])

  const gridOptions = useMemo<GridOptions<RowDataType>>(() => {
    return {
      onTooltipShow: (params) => {
        document.querySelectorAll(".ag-tooltip-active").forEach((el) => {
          if (el !== params?.parentGui) el.remove()
        })
        params?.parentGui?.classList.add("ag-tooltip-active")
      },
      onTooltipHide: (params) => {
        params?.parentGui?.classList.remove("ag-tooltip-active")
      },
      getRowId,
      suppressAggFuncInHeader: true,
      groupDefaultExpanded: 0,
      isGroupOpenByDefault: (params) => expandedGroupKeysRef.current.has(String(params.key)),
      onRowGroupOpened: (event) => {
        const key = String(event.node.key ?? "")
        if (!key) return
        if (event.expanded) expandedGroupKeysRef.current.add(key)
        else expandedGroupKeysRef.current.delete(key)
      },
      singleClickEdit: true,
      suppressAnimationFrame: true,
      suppressColumnMoveAnimation: true,
      suppressRowTransform: true,
      postSortRows: keepSkusInSizeOrder,
    }
  }, [getRowId])

  if (!primaryItems) return null

  const skusHasColliSize = rowData.some((sku) => (sku.settings?.colliSize ?? 0) > 1)
  const skusHasPreorders = rowData.some((sku) => sku.preordersPurchasePeriod > 0)
  const skusHasPreordersBeforeEAD = rowData.some((sku) => sku.preordersBeforeEAD > 0)
  const skusHasSuggestions = suggestedItems?.some((i) => i.quantity > 0) ?? false
  const skusHasUnfulfillableBeforeEAD = !!unfulfillablePreordersFlag && hasUnfulfillableBeforeEAD
  const skusHasUnfulfillableOrAtRiskBeforeEAD =
    !!unfulfillablePreordersFlag && (hasUnfulfillableBeforeEAD || hasAtRiskBeforeEAD)
  const showSyncOptions =
    skusHasColliSize ||
    skusHasPreorders ||
    skusHasPreordersBeforeEAD ||
    skusHasUnfulfillableBeforeEAD

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-4">
      <ViewConfiguration
        tableId={resolvedTableId}
        showFilters={false}
        showWarehouseSelect={false}
        noHorizontalPadding
      />
      <div className="flex flex-row items-center justify-between gap-2">
        <div className="flex flex-row items-center gap-2">
          <DropdownMenu>
            <Tooltip disabled={showSyncOptions} title="No sync options available" side="top">
              <DropdownMenuTrigger asChild disabled={!showSyncOptions}>
                <Button variant="neutral" size="sm" disabled={!showSyncOptions}>
                  Sync options
                </Button>
              </DropdownMenuTrigger>
            </Tooltip>
            <DropdownMenuContent className="**:pointer-events-auto">
              <DropdownMenuLabel>Synchronize quantity</DropdownMenuLabel>
              <Tooltip
                title={
                  skusHasPreorders
                    ? ""
                    : `No ${formatName("preorder", true)} registered for the SKUs`
                }
                side="right"
              >
                <DropdownMenuItem
                  onClick={() => handleSyncQuantityToPreorders("PurchasePeriod")}
                  disabled={!skusHasPreorders}
                >
                  <div className="w-full">With {formatName("preorder", true)}</div>
                </DropdownMenuItem>
              </Tooltip>
              <Tooltip
                title={
                  skusHasPreordersBeforeEAD
                    ? ""
                    : `No ${formatName("preorder", true)} before ETA registered for the SKUs`
                }
                side="right"
              >
                <DropdownMenuItem
                  onClick={() => handleSyncQuantityToPreorders("BeforeEAD")}
                  disabled={!skusHasPreordersBeforeEAD}
                >
                  <div className="flex justify-between w-full items-center gap-2">
                    With {formatName("preorder", true)} before ETA + Suggested Order
                    <InfoTooltip
                      className="h-4 w-4"
                      title={`Synchronize the quantity with the ${formatName("preorder", true)} arriving before ETA (estimated time of arrival) with the Suggested Order quantity added on top, for each SKU.`}
                    />
                  </div>
                </DropdownMenuItem>
              </Tooltip>
              {unfulfillablePreordersFlag && (
                <ShortfallSyncMenuItems
                  primaryItems={primaryItems}
                  rowData={rowData}
                  hasUnfulfillable={skusHasUnfulfillableBeforeEAD}
                  hasUnfulfillableOrAtRisk={skusHasUnfulfillableOrAtRiskBeforeEAD}
                  onSync={handleSyncToUnfulfillablePreorders}
                />
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <ResolveViolationsToolbar
            scope="order"
            moqCount={moqViolationCount}
            colliCount={colliViolationCount}
            onResolveMoq={() => handleResolveMoq()}
            onResolveColli={() => handleResolveColli()}
          />
          <Button
            type="button"
            requires="edit"
            variant="neutral"
            size="sm"
            allowedTitle="Set quantities to the suggested order amounts based on the expected demand for each SKU."
            disabled={!skusHasSuggestions}
            onClick={(e) => {
              resetToSuggestedOrder()
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            Apply suggested orders
          </Button>
          <Tooltip title="Revert all quantity changes back to the last saved values.">
            <Button
              type="button"
              variant="neutral"
              size="sm"
              disabled={!hasQuantityChanges}
              onClick={(e) => {
                discardChanges()
                e.preventDefault()
                e.stopPropagation()
              }}
            >
              Discard changes
            </Button>
          </Tooltip>
        </div>
        <div className="flex flex-row items-center gap-2">
          <ExpandableSearchField
            initialSearchText={searchText}
            onSearchTextChange={setSearchText}
            placeholder="Search variants..."
          />
        </div>
      </div>
      <div className="flex-1 min-h-0">
        <DataTableBaseV2<RowDataType>
          id={resolvedTableId}
          rowIdField="skuId"
          gridRef={gridRef}
          rowData={enrichedRowDataWithStorageLocations}
          columnDefs={columnDefs}
          defaultColumnDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          emptyMessage="No SKUs added"
          loading={(skuIds.length > 0 && skuMap === undefined) || suggestedItems === undefined}
          onCellValueChanged={handleCellValueChanged}
          {...gridOwnedColumnProps}
          gridOptions={gridOptions}
          rowSelection={rowSelection}
          grandTotalRow="pinnedBottom"
          groupHideParentOfSingleChild={false}
          context={tableContext}
          initialSortColumn={{
            name: PurchaseOrderTableColumn.Grouping,
            direction: "asc",
          }}
        />
      </div>
      <MultiEditPurchaseOrderSkuPanel
        selectedCount={selectedSkuIds.length}
        onRemove={handleRemoveSelected}
        onClear={clearSelectedVariants}
        onResolveMoqViolations={() => handleResolveMoq(new Set(selectedSkuIds))}
        onResolveColliViolations={() => handleResolveColli(new Set(selectedSkuIds))}
        partialMoqGroupCount={partialMoqGroupCount}
        groupName={groupName}
      />
    </div>
  )
}
